import React, { useCallback, useEffect, useState } from 'react';
import {
  Baby,
  BookMarked,
  BookOpenCheck,
  BookOpenText,
  Brain,
  Compass,
  Church,
  FlaskConical,
  Flame,
  Ghost,
  Heart,
  HeartPulse,
  Landmark,
  Map,
  Minus,
  Moon,
  Palette,
  PenLine,
  Rocket,
  Search,
  Smile,
  Sparkles,
  Sun,
  Target,
  ThumbsDown,
  ThumbsUp,
  TriangleAlert,
  User,
  WandSparkles,
  X,
  Zap,
} from 'lucide-react';
import './App.css';
import {
  getRecommendations,
  getSampleBooks,
  THEME_TAG_MAPPING,
} from './bookDatabase';

// API endpoints. In production these are same-origin Vercel functions; for local
// development set REACT_APP_API_BASE (see .env.development) so all data flows
// work against the deployed API, since react-scripts can't run /api functions.
const API_BASE = process.env.REACT_APP_API_BASE
  || (process.env.REACT_APP_LISTINGS_API ? new URL(process.env.REACT_APP_LISTINGS_API).origin : '');
const LISTINGS_API = process.env.REACT_APP_LISTINGS_API || `${API_BASE}/api/listings`;
const RECOMMEND_API = `${API_BASE}/api/recommend`;
const THEMES_API = `${API_BASE}/api/scrapers/themes`;

// Genre categories for browsing
const GENRES = [
  { id: 'fiction', name: 'Fiction', icon: BookOpenText },
  { id: 'scifi', name: 'Sci-Fi', icon: Rocket },
  { id: 'fantasy', name: 'Fantasy', icon: Sparkles },
  { id: 'mystery', name: 'Mystery', icon: Search },
  { id: 'romance', name: 'Romance', icon: Heart },
  { id: 'horror', name: 'Horror', icon: Ghost },
  { id: 'manga', name: 'Manga', icon: BookMarked },
  { id: 'history', name: 'History', icon: Landmark },
  { id: 'childrens', name: "Children's", icon: Baby },
  { id: 'science', name: 'Science', icon: FlaskConical },
  { id: 'biography', name: 'Biography', icon: User },
  { id: 'poetry', name: 'Poetry', icon: PenLine },
  { id: 'philosophy', name: 'Philosophy', icon: Brain },
  { id: 'art', name: 'Art', icon: Palette },
  { id: 'religion', name: 'Religion', icon: Church },
  { id: 'travel', name: 'Travel', icon: Map },
];

// Genres available in the Discovery tool - limited to genres that have
// curated books in bookDatabase.js so step 2 always has books to show
const DISCOVERY_GENRES = [
  { id: 'fiction', name: 'Fiction', icon: BookOpenText },
  { id: 'scifi', name: 'Sci-Fi', icon: Rocket },
  { id: 'fantasy', name: 'Fantasy', icon: Sparkles },
  { id: 'mystery', name: 'Mystery', icon: Search },
  { id: 'thriller', name: 'Thriller', icon: Zap },
  { id: 'romance', name: 'Romance', icon: Heart },
  { id: 'horror', name: 'Horror', icon: Ghost },
  { id: 'history', name: 'Historical', icon: Landmark },
  { id: 'literary', name: 'Literary', icon: PenLine },
];

const getDiscoveryGenreName = (id) =>
  DISCOVERY_GENRES.find((g) => g.id === id)?.name || GENRES.find((g) => g.id === id)?.name || id;

// Genres that have a curated popular-titles list in the API (api/listings.js).
// Other genres fall back to a plain eBay browse so they still show results.
const POPULAR_GENRE_IDS = new Set([
  'fiction', 'scifi', 'fantasy', 'mystery', 'romance',
  'horror', 'manga', 'history', 'childrens', 'biography',
]);

// Reading themes for discovery
const READING_THEMES = [
  { id: 'adventure', name: 'Adventure', icon: Compass },
  { id: 'romance', name: 'Romance', icon: Heart },
  { id: 'dark', name: 'Dark & Gritty', icon: Moon },
  { id: 'heartwarming', name: 'Heartwarming', icon: Sun },
  { id: 'thought-provoking', name: 'Thought-Provoking', icon: Brain },
  { id: 'fast-paced', name: 'Fast-Paced', icon: Zap },
  { id: 'slow-burn', name: 'Slow Burn', icon: Flame },
  { id: 'magical', name: 'Magical', icon: Sparkles },
  { id: 'historical', name: 'Historical', icon: Landmark },
  { id: 'suspenseful', name: 'Suspenseful', icon: TriangleAlert },
  { id: 'humorous', name: 'Humorous', icon: Smile },
  { id: 'emotional', name: 'Emotional', icon: HeartPulse },
];

const THEME_NAME_BY_ID = READING_THEMES.reduce((acc, theme) => {
  acc[theme.id] = theme.name;
  return acc;
}, {});

const GARBAGE_TITLE_PATTERNS = [
  /^(fantasy|scifi|science fiction|mystery|romance|horror|fiction|history|biography|poetry|philosophy|art|religion|travel)\s*(books?|novels?|stories?)?$/i,
  /^(best|top|great|amazing|awesome)\s+(books?|novels?|reads?)\b/i,
  /^books?\s+(for|about|on)\s+/i,
  /^how\s+to\s+/i,
  /^the\s+(complete|ultimate|best)\s+(guide|collection)/i,
  /(coloring\s*book|activity\s*book|workbook|journal|planner|log\s*book|notebook)\b/i,
  /(summary|study\s*guide|analysis|lesson\s*plan|answer\s*key)\b/i,
  /(box\s*set|boxed\s*set|collection|omnibus|anthology)\b/i,
  // Filter out non-fiction reference material
  /(encyclopedia|dictionary|handbook|manual|companion|atlas)\b/i,
  /(guide\s+to|introduction\s+to|history\s+of|making\s+of)\b/i,
  /(reread|re-read|rereading)\b/i,
  /(sampler|preview|excerpt|teaser)\b/i,
  /\b(uncle\s+john|bathroom\s+reader|trivia|quiz)\b/i,
  /\bby\s+.*\bAI\b/i,
  /(deluxe\s+edition|anniversary\s+edition|collector'?s?\s+edition|special\s+edition)\b/i,
  /\b(part\s+(one|two|three|four|1|2|3|4))\b/i,
  /(growth|analysis|criticism|theory|studies)\b/i,
  /\b(100|50|25|10)\s+(best|top|greatest)\b/i,
  /(illustrated|graphic)\s+(guide|edition|companion)\b/i,
];

const normalizeWhitespace = (value = '') => value.replace(/\s+/g, ' ').trim();

const stripHtml = (value = '') => normalizeWhitespace(value.replace(/<[^>]*>/g, ' '));

const titleCase = (value = '') =>
  value
    .split(' ')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');

const buildConciseDescription = (value = '', maxLength = 160) => {
  const clean = normalizeWhitespace(stripHtml(value));
  if (!clean) return '';
  if (clean.length <= maxLength) return clean;
  return `${clean.slice(0, maxLength).replace(/\s+\S*$/, '')}…`;
};

const inferThemeIdsFromBook = (book) => {
  const tags = (book.tags || []).map((tag) => String(tag).toLowerCase());
  const description = normalizeWhitespace(stripHtml(book.description || '')).toLowerCase();
  const themeIds = new Set();

  Object.entries(THEME_TAG_MAPPING).forEach(([themeId, keywords]) => {
    const matches = keywords.some((keyword) => {
      const lowered = keyword.toLowerCase();
      return tags.some((tag) => tag.includes(lowered)) || description.includes(lowered);
    });
    if (matches) themeIds.add(themeId);
  });

  return [...themeIds];
};

const inferCoreThemes = (book) => {
  const themeIds = inferThemeIdsFromBook(book);
  if (themeIds.length > 0) {
    return themeIds.map((id) => THEME_NAME_BY_ID[id]).filter(Boolean);
  }
  const fallbackTags = (book.tags || []).slice(0, 3).map((tag) => titleCase(String(tag)));
  return fallbackTags;
};

const normalizeBookRecord = (book) => {
  const description = normalizeWhitespace(stripHtml(book.description || ''));
  return {
    ...book,
    title: normalizeWhitespace(book.title || ''),
    author: normalizeWhitespace(book.author || ''),
    description,
    shortDescription: buildConciseDescription(description),
    coreThemes: inferCoreThemes({ ...book, description }),
  };
};

const isValidBookRecord = (book) => {
  if (!book) return false;
  const title = normalizeWhitespace(book.title || '');
  const author = normalizeWhitespace(book.author || '');
  const description = normalizeWhitespace(stripHtml(book.description || ''));

  if (!title || title.length < 3 || title.length > 140) return false;
  if (GARBAGE_TITLE_PATTERNS.some((pattern) => pattern.test(title))) return false;
  if (!author || author.length < 3 || author.length > 120) return false;
  if (/\banonymous\b/i.test(author)) return false;
  if (!description || description.length < 30) return false;
  if (book.year && (book.year < 1400 || book.year > new Date().getFullYear() + 1)) return false;
  const coreThemes = book.coreThemes || inferCoreThemes(book);
  return coreThemes.length > 0;
};

// Fun loading messages that cycle
const LOADING_MESSAGES = [
  'Brewing some coffee...',
  'Dusting off the shelves...',
  'Consulting the librarian...',
  'Flipping through pages...',
  'Checking the card catalog...',
  'Wandering the stacks...',
  'Adjusting reading glasses...',
  'Opening ancient tomes...',
  'Following the paper trail...',
  'Decoding the Dewey Decimal...',
  'Asking the book wizard...',
  'Polishing the monocle...',
  'Lighting the reading lamp...',
  'Unfurling the scroll...',
  'Consulting first editions...',
];

// Discovery step names
const DISCOVERY_STEPS = [
  { num: 1, name: 'Genres' },
  { num: 2, name: 'Pick Books' },
  { num: 3, name: 'Results' },
];

// Book Cover component with fallback placeholder
function BookCover({ src, title, author, className = '' }) {
  const [hasError, setHasError] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  
  // Generate a color based on title for placeholder
  const hash = (title || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const hue = hash % 360;
  
  const handleError = () => {
    setHasError(true);
  };
  
  const handleLoad = (e) => {
    // Check if the image is actually a placeholder (very small dimensions often indicate this)
    const img = e.target;
    if (img.naturalWidth < 10 || img.naturalHeight < 10) {
      setHasError(true);
    } else {
      setLoaded(true);
    }
  };
  
  if (hasError || !src) {
    // Render placeholder
    return (
      <div 
        className={`book-cover-placeholder ${className}`}
        style={{
          background: `linear-gradient(135deg, hsl(${hue}, 35%, 30%) 0%, hsl(${hue}, 40%, 20%) 100%)`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '12px',
          textAlign: 'center',
          color: '#fff',
          height: '100%',
          minHeight: '180px',
          borderRadius: '4px',
          border: `2px solid hsl(${hue}, 30%, 40%)`,
        }}
      >
        <div style={{ 
          fontSize: '11px', 
          fontWeight: 'bold', 
          lineHeight: '1.3',
          marginBottom: '8px',
          textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
          maxHeight: '80px',
          overflow: 'hidden',
        }}>
          {title || 'Unknown Title'}
        </div>
        <div style={{ 
          fontSize: '9px', 
          opacity: 0.8,
          fontStyle: 'italic',
        }}>
          {author || 'Unknown Author'}
        </div>
      </div>
    );
  }
  
  return (
    <img 
      src={src} 
      alt={title} 
      className={className}
      onError={handleError}
      onLoad={handleLoad}
      style={{ opacity: loaded ? 1 : 0.5 }}
    />
  );
}

// Book Detail Modal
function BookDetailModal({ book, onClose }) {
  // Close on Escape key
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden'; // Prevent background scroll
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!book) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content">
        <button className="modal-close" onClick={onClose}>×</button>
        
        <div className="modal-body">
          <div className="modal-image">
            <BookCover src={book.imageUrl} title={book.title} author={book.author} />
          </div>
          
          <div className="modal-details">
            <h2 className="modal-title">{book.title}</h2>
            {book.author && <p className="modal-author">by {book.author}</p>}
            
            <div className="modal-meta-grid">
              {book.price && (
                <div className="modal-meta-item">
                  <span className="modal-meta-label">Price</span>
                  <span className="modal-meta-value price">
                    {typeof book.price === 'number'
                      ? `$${book.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : book.price}
                  </span>
                </div>
              )}
              
              {book.source && (
                <div className="modal-meta-item">
                  <span className="modal-meta-label">Seller</span>
                  <span className="modal-meta-value">{book.source}</span>
                </div>
              )}
              
              {book.year && (
                <div className="modal-meta-item">
                  <span className="modal-meta-label">Year</span>
                  <span className="modal-meta-value">{book.year}</span>
                </div>
              )}
              
              {book.isbn && (
                <div className="modal-meta-item">
                  <span className="modal-meta-label">ISBN</span>
                  <span className="modal-meta-value mono">{book.isbn}</span>
                </div>
              )}
              
              {book.format && (
                <div className="modal-meta-item">
                  <span className="modal-meta-label">Format</span>
                  <span className="modal-meta-value">{book.format}</span>
                </div>
              )}
              
              {book.condition && (
                <div className="modal-meta-item">
                  <span className="modal-meta-label">Condition</span>
                  <span className="modal-meta-value">{book.condition}</span>
                </div>
              )}
              
              {book.editions && (
                <div className="modal-meta-item">
                  <span className="modal-meta-label">Editions</span>
                  <span className="modal-meta-value">{book.editions} available</span>
                </div>
              )}
            </div>
            
            {book.tags && book.tags.length > 0 && (
              <div className="modal-tags">
                {book.tags.map((tag) => (
                  <span key={tag} className="book-tag">{tag}</span>
                ))}
              </div>
            )}
            
            {book.description && (
              <div className="modal-description">
                <h4>Description</h4>
                <p>{book.description}</p>
              </div>
            )}
            
            <div className="modal-actions">
              {book.link && (
                <a href={book.link} target="_blank" rel="noreferrer" className="modal-btn primary">
                  Visit Seller →
                </a>
              )}
              <button className="modal-btn secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ListingCard({ listing, onClick, onFindEditions }) {
  const formatPrice = (price) => {
    if (!price) return null;
    if (typeof price === 'number') return `$${price.toFixed(2)}`;
    if (typeof price === 'string' && price.startsWith('$')) return price;
    return `$${price}`;
  };

  const handleFindEditions = (e) => {
    e.stopPropagation();
    if (onFindEditions) onFindEditions(listing);
  };

  return (
    <div
      className="book-card"
      role="button"
      tabIndex={0}
      onClick={() => onClick(listing)}
      onKeyDown={cardKeyHandler(() => onClick(listing))}
      title="Click to view details"
    >
      <div className="book-image">
        {listing.imageUrl ? (
          <img src={listing.imageUrl} alt={listing.title} className="book-cover-img" />
        ) : (
          <div className="book-spine book-spine-1">
            <span className="book-spine-text">{listing.title}</span>
          </div>
        )}
        {listing.badge && <span className="book-badge">{listing.badge}</span>}
        {listing.price && (
          <span className="book-price-badge">{formatPrice(listing.price)}</span>
        )}
      </div>
      <div className="book-content">
        <h3 className="book-title">{listing.title}</h3>
        {listing.author && <p className="book-author">{listing.author}</p>}
        <div className="book-meta">
          {listing.source && <span className="book-tag seller">{listing.source}</span>}
          {listing.year && <span className="book-tag">{listing.year}</span>}
          {listing.format && <span className="book-tag format">{listing.format}</span>}
          {listing.condition && <span className="book-tag condition">{listing.condition}</span>}
        </div>
        <div className="book-card-footer">
          {listing.seller && (
            <span className="seller-name">Seller: {listing.seller}</span>
          )}
          {onFindEditions && (
            <button className="find-editions-btn" onClick={handleFindEditions}>
              Find Editions
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const renderIcon = (Icon, className) =>
  Icon ? (
    <span className={className} aria-hidden="true">
      <Icon />
    </span>
  ) : null;

// Keyboard support for clickable card divs (Enter/Space activate like a button)
const cardKeyHandler = (action) => (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    action();
  }
};

function GenreCard({ genre, onClick }) {
  return (
    <div
      className="genre-card"
      role="button"
      tabIndex={0}
      onClick={() => onClick(genre.id)}
      onKeyDown={cardKeyHandler(() => onClick(genre.id))}
    >
      {renderIcon(genre.icon, 'genre-icon')}
      <span className="genre-name">{genre.name}</span>
    </div>
  );
}

function App() {
  // Initialize view from URL hash
  const getInitialView = () => {
    const hash = window.location.hash.slice(1);
    if (hash.startsWith('browse')) return 'browse';
    if (hash.startsWith('search')) return 'search';
    if (hash.startsWith('discover')) return 'discover';
    if (hash.startsWith('about')) return 'about';
    return 'home';
  };

  const getInitialCategory = () => {
    const hash = window.location.hash.slice(1);
    if (hash.startsWith('browse/')) {
      return hash.replace('browse/', '');
    }
    return '';
  };

  const getInitialQuery = () => {
    const hash = window.location.hash.slice(1);
    if (hash.startsWith('search/')) {
      return decodeURIComponent(hash.replace('search/', ''));
    }
    return '';
  };

  const [query, setQuery] = useState(getInitialQuery());
  const [searchQuery, setSearchQuery] = useState(getInitialQuery());
  const [selectedCategory, setSelectedCategory] = useState(getInitialCategory());
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Advanced search fields
  const [authorField, setAuthorField] = useState('');
  const [titleField, setTitleField] = useState('');
  const [keywordsField, setKeywordsField] = useState('');
  const [isbnField, setIsbnField] = useState('');
  const [publisherField, setPublisherField] = useState('');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [conditionFilter, setConditionFilter] = useState('');
  
  // Filter checkboxes
  const [firstEdition, setFirstEdition] = useState(false);
  const [signedCopy, setSignedCopy] = useState(false);
  const [dustJacket, setDustJacket] = useState(false);
  const [imageOnly, setImageOnly] = useState(false);
  const [excludePaperback, setExcludePaperback] = useState(false);
  const [excludeExLibrary, setExcludeExLibrary] = useState(false);
  const [excludeBookClub, setExcludeBookClub] = useState(false);
  const [advancedGenres, setAdvancedGenres] = useState([]);
  const [popularOnly, setPopularOnly] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [browseRefineOptions, setBrowseRefineOptions] = useState({ firstEdition: false, signed: false }); // Browse section refine filters
  
  const [wantName, setWantName] = useState(''); // eslint-disable-line no-unused-vars
  const [wants, setWants] = useState([]); // eslint-disable-line no-unused-vars
  const [listings, setListings] = useState([]);
  const [isGroupedResults, setIsGroupedResults] = useState(false);  // Whether results have grouping info
  const [selectedTitleGroup, setSelectedTitleGroup] = useState(null);  // Filter to show only this title's results
  const [selectedTitleAuthor, setSelectedTitleAuthor] = useState(null);  // Author for direct title search
  const [titleListings, setTitleListings] = useState([]);  // Listings when viewing a specific title
  const [titleOffset, setTitleOffset] = useState(0);  // Offset for title-specific pagination
  const [titleHasMore, setTitleHasMore] = useState(true);  // Whether more results are available for title
  const [titleLoading, setTitleLoading] = useState(false);  // Loading state for title-specific search
  const [browseAllMode, setBrowseAllMode] = useState(false);  // Skip popular titles, show all eBay results
  const [homepageGenres, setHomepageGenres] = useState({});
  const [homepageLoading, setHomepageLoading] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState(getInitialView());
  const [selectedBook, setSelectedBook] = useState(null);
  const [sortBy, setSortBy] = useState('relevance');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // Discovery tool state
  const [discoveryStep, setDiscoveryStep] = useState(1);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [sampleBooks, setSampleBooks] = useState([]);
  const [scrapingProgress, setScrapingProgress] = useState({ current: 0, total: 0 }); // Track scraping progress
  const [likedBooks, setLikedBooks] = useState([]); // Books user has "read"
  const [bookSentiments, setBookSentiments] = useState({}); // {bookId: 'loved'|'liked'|'neutral'|'disliked'}
  const [wantToReadBooks, setWantToReadBooks] = useState([]); // Books user wants to read (go to results)
  const [dislikedBooks, setDislikedBooks] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [myList, setMyList] = useState([]); // Books user adds to their list from recommendations
  const [listCopied, setListCopied] = useState(false); // Brief "copied" feedback on the copy button
  const [expandedDescriptions, setExpandedDescriptions] = useState({}); // Track which descriptions are expanded

  // Theme state - 'light' or 'dark'
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('biblioquire-theme');
    if (saved === 'light' || saved === 'dark') return saved;
    // Default to system preference on first visit
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light-theme', 'dark-theme');
    root.classList.add(`${theme}-theme`);
    localStorage.setItem('biblioquire-theme', theme);
  }, [theme]);

  // Toggle between light and dark themes
  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  // Get theme icon for display
  const getThemeIcon = () => {
    return theme === 'light' ? '☀️' : '🌙';
  };

  // Toggle description expansion
  const toggleDescriptionExpand = (bookId, e) => {
    if (e) e.stopPropagation();
    setExpandedDescriptions(prev => ({
      ...prev,
      [bookId]: !prev[bookId]
    }));
  };

  // Edition picker state (for selecting specific book editions in search results)
  const [showEditionPicker, setShowEditionPicker] = useState(false);
  const [editionPickerBook, setEditionPickerBook] = useState(null);
  const [editionResults, setEditionResults] = useState([]);
  const [editionLoading, setEditionLoading] = useState(false);

  const ITEMS_PER_PAGE = 48;

  // Sync URL hash with view state
  useEffect(() => {
    let hash = view;
    if (view === 'browse' && selectedCategory) {
      hash = `browse/${selectedCategory}`;
    } else if (view === 'search' && searchQuery) {
      hash = `search/${encodeURIComponent(searchQuery)}`;
    }
    if (window.location.hash !== `#${hash}`) {
      window.history.pushState(null, '', `#${hash}`);
    }
  }, [view, selectedCategory, searchQuery]);

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const hash = window.location.hash.slice(1);
      if (hash.startsWith('browse/')) {
        setView('browse');
        setSelectedCategory(hash.replace('browse/', ''));
      } else if (hash.startsWith('search/')) {
        setView('search');
        const q = decodeURIComponent(hash.replace('search/', ''));
        setQuery(q);
        setSearchQuery(q);
      } else if (hash === 'discover') {
        setView('discover');
      } else if (hash === 'browse') {
        setView('browse');
        setSelectedCategory('');
      } else if (hash === 'search') {
        setView('search');
      } else if (hash === 'about') {
        setView('about');
      } else {
        setView('home');
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Cycle loading messages when discoveryLoading is true
  useEffect(() => {
    if (!discoveryLoading) return;
    
    const interval = setInterval(() => {
      setLoadingMessageIndex(prev => (prev + 1) % LOADING_MESSAGES.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [discoveryLoading]);

  // Show scroll-to-top button when scrolled down
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fetch listings from API
  const fetchListings = useCallback(async (controller, options = {}) => {
    const { category, q, featured, limit = 24, offset = 0, returnFullResponse = false, browsePopular = false } = options;
    
    try {
      const endpoint = LISTINGS_API;
      const params = new URLSearchParams({ limit: String(limit), sort: 'newest', offset: String(offset) });
      
      if (q) params.append('q', q);
      if (category && !browsePopular) params.append('category', category);
      if (featured) params.append('featured', '1');
      if (imageOnly) params.append('hasImage', '1');
      if (conditionFilter) params.append('condition', conditionFilter);
      if (minPrice) params.append('minPrice', minPrice);
      if (maxPrice) params.append('maxPrice', maxPrice);
      if (yearFrom) params.append('yearFrom', yearFrom);
      if (yearTo) params.append('yearTo', yearTo);
      if (firstEdition) params.append('firstEdition', '1');
      if (signedCopy) params.append('signed', '1');
      if (dustJacket) params.append('dustJacket', '1');
      if (excludePaperback) params.append('excludePaperback', '1');
      if (excludeExLibrary) params.append('excludeExLibrary', '1');
      if (excludeBookClub) params.append('excludeBookClub', '1');
      
      // Browse popular mode: use category as genre for popular titles
      if (browsePopular && category) {
        params.append('popularOnly', '1');
        params.append('genres', category);
      } else if (popularOnly) {
        params.append('popularOnly', '1');
        if (advancedGenres.length > 0) {
          params.append('genres', advancedGenres.join(','));
        }
      }
      
      const url = `${endpoint}?${params.toString()}`;
      const res = await fetch(url, { signal: controller?.signal });
      
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      
      const data = await res.json();
      // Return full response if requested (for handling hasMore from popular searches)
      if (returnFullResponse) {
        return data;
      }
      return data.items || [];
    } catch (err) {
      if (err.name === 'AbortError') return returnFullResponse ? { items: [] } : [];
      console.error('Fetch error:', err);
      throw err;
    }
  }, [imageOnly, conditionFilter, minPrice, maxPrice, yearFrom, yearTo, firstEdition, signedCopy, dustJacket, excludePaperback, excludeExLibrary, excludeBookClub, popularOnly, advancedGenres]);

  // Homepage genre sections configuration
  const HOMEPAGE_GENRES = [
    { id: 'fiction', name: 'Fiction', query: 'fiction first edition vintage', icon: BookOpenText },
    { id: 'scifi', name: 'Science Fiction', query: 'science fiction first edition rare', icon: Rocket },
    { id: 'fantasy', name: 'Fantasy', query: 'fantasy first edition vintage', icon: Sparkles },
    { id: 'mystery', name: 'Mystery & Thriller', query: 'mystery thriller first edition vintage', icon: Search },
    { id: 'history', name: 'History', query: 'history antique rare book', icon: Landmark },
    { id: 'biography', name: 'Biography', query: 'biography memoir first edition signed', icon: User },
  ];

  // Load homepage featured sections - load each genre independently
  useEffect(() => {
    if (view !== 'home') return;
    
    // Load each genre section independently
    HOMEPAGE_GENRES.forEach(async (genre) => {
      // Skip if already loaded
      if (homepageGenres[genre.id]?.length > 0) return;
      
      const controller = new AbortController();
      setHomepageLoading(prev => ({ ...prev, [genre.id]: true }));
      
      try {
        const books = await fetchListings(controller, { category: genre.query, limit: 12 });
        setHomepageGenres(prev => ({ ...prev, [genre.id]: books }));
      } catch (err) {
        console.error(`Error loading ${genre.name}:`, err);
        setHomepageGenres(prev => ({ ...prev, [genre.id]: [] }));
      } finally {
        setHomepageLoading(prev => ({ ...prev, [genre.id]: false }));
      }
    });
    
    setLoading(false);
  }, [view, fetchListings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load search results or category browse (initial load)
  useEffect(() => {
    const controller = new AbortController();
    
    async function loadResults() {
      if (view === 'home') return;
      
      setLoading(true);
      setError('');
      setOffset(0);
      setHasMore(true);
      setIsGroupedResults(false);
      setSelectedTitleGroup(null);
      
      try {
        let response;
        if (view === 'search' && searchQuery) {
          response = await fetchListings(controller, { q: searchQuery, limit: 500, offset: 0, returnFullResponse: true });
        } else if (view === 'browse' && selectedCategory) {
          if (browseAllMode) {
            // Simple eBay search for the genre (no popular titles grouping)
            response = await fetchListings(controller, { 
              category: selectedCategory, 
              limit: 100, 
              offset: 0, 
              returnFullResponse: true,
              browsePopular: false
            });
          } else {
            // Browse uses popular titles grouped view
            response = await fetchListings(controller, { 
              category: selectedCategory, 
              limit: 1000, 
              offset: 0, 
              returnFullResponse: true,
              browsePopular: true
            });
          }
        } else {
          response = { items: [] };
        }
        const results = response.items || [];
        setListings(results);
        const grouped = response.isGrouped || false;
        setIsGroupedResults(grouped);
        // Continue paging after the results we already have
        setOffset(results.length);
        // Grouped popular results are fetched all at once; plain results can page
        setHasMore(!grouped && results.length >= ITEMS_PER_PAGE);
      } catch (err) {
        setError('Search failed. Please try again.');
        setListings([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    }
    
    loadResults();
    return () => controller.abort();
  }, [view, searchQuery, selectedCategory, browseAllMode, fetchListings]);

  // Load more results when scrolling near bottom (infinite scroll)
  const loadMoreResults = useCallback(async () => {
    if (loadingMore || !hasMore || view === 'home') return;
    
    setLoadingMore(true);
    // `offset` tracks how many results we've already requested
    const newOffset = offset;

    try {
      let response;
      if (view === 'search' && searchQuery) {
        response = await fetchListings(null, { q: searchQuery, limit: ITEMS_PER_PAGE, offset: newOffset, returnFullResponse: true });
      } else if (view === 'browse' && selectedCategory) {
        response = await fetchListings(null, { category: selectedCategory, limit: ITEMS_PER_PAGE, offset: newOffset, returnFullResponse: true });
      } else {
        response = { items: [] };
      }

      const moreResults = response.items || [];

      if (moreResults.length > 0) {
        // Filter out duplicates based on link or title
        const existingLinks = new Set(listings.map(l => l.link));
        const uniqueResults = moreResults.filter(r => !existingLinks.has(r.link));
        setListings(prev => [...prev, ...uniqueResults]);
        setOffset(newOffset + moreResults.length);
        setHasMore(moreResults.length >= ITEMS_PER_PAGE);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error loading more:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, view, searchQuery, selectedCategory, offset, listings, fetchListings]);

  // Load more results for a specific title (moved before infinite scroll listener)
  const loadMoreTitleResults = useCallback(async () => {
    if (titleLoading || !titleHasMore || !selectedTitleGroup) return;
    
    setTitleLoading(true);
    const newOffset = titleOffset + 48;
    
    try {
      const searchTerm = selectedTitleAuthor 
        ? `${selectedTitleGroup} ${selectedTitleAuthor}` 
        : selectedTitleGroup;
      const endpoint = LISTINGS_API;
      const params = new URLSearchParams({ 
        q: searchTerm, 
        limit: '48', 
        offset: String(newOffset),
        sort: 'newest' 
      });
      
      const res = await fetch(`${endpoint}?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      
      const moreResults = data.items || [];
      
      if (moreResults.length > 0) {
        // Filter out duplicates
        const existingLinks = new Set(titleListings.map(l => l.link));
        const uniqueResults = moreResults.filter(r => !existingLinks.has(r.link));
        setTitleListings(prev => [...prev, ...uniqueResults]);
        setTitleOffset(newOffset);
        setTitleHasMore(moreResults.length >= 48);
      } else {
        setTitleHasMore(false);
      }
    } catch (err) {
      console.error('Error loading more title results:', err);
    } finally {
      setTitleLoading(false);
    }
  }, [titleLoading, titleHasMore, selectedTitleGroup, selectedTitleAuthor, titleOffset, titleListings]);

  // Infinite scroll listener
  useEffect(() => {
    const handleScroll = () => {
      if (view === 'home' || loading || loadingMore) return;
      
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      
      // Load more when user is within 500px of the bottom
      if (scrollTop + windowHeight >= docHeight - 500) {
        // If viewing a specific title in browse mode, use title pagination
        if (view === 'browse' && selectedTitleGroup && !browseAllMode && titleHasMore && !titleLoading) {
          loadMoreTitleResults();
        } else if (hasMore) {
          loadMoreResults();
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [view, loading, loadingMore, hasMore, loadMoreResults, selectedTitleGroup, browseAllMode, titleHasMore, titleLoading, loadMoreTitleResults]);

  // Build search query from all fields
  const buildSearchQuery = () => {
    const parts = [];
    
    // Add main query
    if (query.trim()) parts.push(query.trim());
    
    // Add author
    if (authorField.trim()) parts.push(authorField.trim());
    
    // Add title
    if (titleField.trim()) parts.push(titleField.trim());
    
    // Add keywords (comma-separated, just joined with spaces)
    if (keywordsField.trim()) {
      const keywords = keywordsField.split(',').map(k => k.trim()).filter(k => k);
      if (keywords.length > 0) {
        parts.push(keywords.join(' '));
      }
    }
    
    // Add selected genres
    if (advancedGenres.length > 0) {
      const genreNames = advancedGenres.map(gId => {
        const genre = GENRES.find(g => g.id === gId);
        return genre ? genre.name : '';
      }).filter(n => n);
      if (genreNames.length > 0) {
        parts.push(genreNames.join(' '));
      }
    }
    
    // Add ISBN
    if (isbnField.trim()) parts.push(isbnField.trim());
    
    // Add publisher
    if (publisherField.trim()) parts.push(publisherField.trim());
    
    // Add special keywords for filters
    if (firstEdition) parts.push('first edition');
    if (signedCopy) parts.push('signed');
    if (dustJacket) parts.push('dust jacket');
    
    return parts.join(' ').trim();
  };

  // Search for editions of a book by title (for edition picker)
  const searchEditions = async (bookTitle, bookAuthor) => {
    setEditionLoading(true);
    setEditionResults([]);
    try {
      const searchTerm = bookAuthor ? `${bookTitle} ${bookAuthor}` : bookTitle;
      const response = await fetch(`${LISTINGS_API}?q=${encodeURIComponent(searchTerm)}&limit=48`);
      const data = await response.json();
      if (data.items) {
        setEditionResults(data.items);
      }
    } catch (error) {
      console.error('Error fetching editions:', error);
    }
    setEditionLoading(false);
  };

  // Open edition picker for a specific book
  const openEditionPicker = (book) => {
    setEditionPickerBook(book);
    setShowEditionPicker(true);
    // Extract title/author for searching
    const title = book.title?.split(' by ')[0]?.split(' - ')[0] || book.title;
    const author = book.author || '';
    searchEditions(title, author);
  };

  const closeEditionPicker = () => {
    setShowEditionPicker(false);
    setEditionPickerBook(null);
    setEditionResults([]);
  };

  const handleSearch = () => {
    const searchStr = buildSearchQuery();
    if (searchStr) {
      setSearchQuery(searchStr);
      setView('search');
      setShowAdvanced(false); // Close advanced search after searching
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleGenreClick = (genreId) => {
    setSelectedCategory(genreId);
    // Genres with a curated list get the grouped popular-titles view;
    // others fall back to a plain eBay browse so they still show results
    setBrowseAllMode(!POPULAR_GENRE_IDS.has(genreId));
    setSelectedTitleGroup(null);
    setSelectedTitleAuthor(null);
    setTitleListings([]);
    setTitleOffset(0);
    setTitleHasMore(true);
    setBrowseRefineOptions({ firstEdition: false, signed: false }); // Reset refine options
    setView('browse');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle clicking on a specific title group - fetch fresh results from eBay
  const handleTitleGroupClick = async (title, author) => {
    setSelectedTitleGroup(title);
    setSelectedTitleAuthor(author || null);
    setTitleListings([]);
    setTitleOffset(0);
    setTitleHasMore(true);
    setTitleLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    try {
      // Search eBay for this specific title + author
      const searchTerm = author ? `${title} ${author}` : title;
      const endpoint = LISTINGS_API;
      const params = new URLSearchParams({ 
        q: searchTerm, 
        limit: '48', 
        offset: '0',
        sort: 'newest' 
      });
      
      const res = await fetch(`${endpoint}?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      
      const results = data.items || [];
      setTitleListings(results);
      setTitleHasMore(results.length >= 48);
    } catch (err) {
      console.error('Error fetching title listings:', err);
      setTitleListings([]);
      setTitleHasMore(false);
    } finally {
      setTitleLoading(false);
    }
  };

  const handleCardClick = (book) => {
    setSelectedBook(book);
  };

  const goHome = () => {
    setView('home');
    setQuery('');
    setSearchQuery('');
    setSelectedCategory('');
  };

  const resetAdvancedSearch = () => {
    setAuthorField('');
    setTitleField('');
    setKeywordsField('');
    setIsbnField('');
    setPublisherField('');
    setYearFrom('');
    setYearTo('');
    setMinPrice('');
    setMaxPrice('');
    setConditionFilter('');
    setFirstEdition(false);
    setSignedCopy(false);
    setDustJacket(false);
    setImageOnly(false);
    setExcludePaperback(false);
    setExcludeExLibrary(false);
    setExcludeBookClub(false);
    setAdvancedGenres([]);
    setPopularOnly(false);
  };

  // When user selects a specific edition from edition picker
  const selectEdition = (book) => {
    // Go directly to the book's listing URL if available
    const listingUrl = book.link || book.url;
    if (listingUrl) {
      window.open(listingUrl, '_blank', 'noopener');
    }
    closeEditionPicker();
  };

  // Discovery tool functions
  const startDiscovery = () => {
    setView('discover');
    setDiscoveryStep(1);
    setSelectedGenres([]);
    setLikedBooks([]);
    setBookSentiments({});
    setWantToReadBooks([]);
    setDislikedBooks([]);
    setSampleBooks([]);
    setRecommendations([]);
    setMyList([]);
  };

  // Navigate to a specific discovery step (for clickable dots)
  const goToDiscoveryStep = (step) => {
    // Can only go back, not forward (except step 1)
    if (step < discoveryStep || step === 1) {
      setDiscoveryStep(step);
    }
  };

  // Check if a step is accessible (completed or current)
  const isStepAccessible = (step) => {
    if (step === 1) return true;
    if (step === 2) return selectedGenres.length > 0;
    if (step === 3) return recommendations.length > 0 || wantToReadBooks.length > 0;
    return false;
  };

  const toggleGenreSelection = (genreId) => {
    setSelectedGenres(prev => {
      if (prev.includes(genreId)) {
        return prev.filter(g => g !== genreId);
      }
      return prev.length < 5 ? [...prev, genreId] : prev;
    });
  };

  // ========== WEB SCRAPING FOR DYNAMIC THEME DETECTION ==========
  
  // Cache management
  const getThemeCache = (bookId) => {
    try {
      const cached = localStorage.getItem(`theme_cache_${bookId}`);
      if (!cached) return null;
      
      const data = JSON.parse(cached);
      const expiresAt = new Date(data.expires);
      if (expiresAt < new Date()) {
        localStorage.removeItem(`theme_cache_${bookId}`);
        return null;
      }
      return data.themes;
    } catch (error) {
      return null;
    }
  };

  const setThemeCache = (bookId, themes) => {
    try {
      const expires = new Date();
      expires.setDate(expires.getDate() + 30); // 30 day cache
      localStorage.setItem(`theme_cache_${bookId}`, JSON.stringify({
        themes,
        expires: expires.toISOString()
      }));
    } catch (error) {
      console.error('Failed to cache themes:', error);
    }
  };

  // Main scraping function - now calls backend API to avoid CORS
  const fetchThemesMultiSource = async (title, author, bookId) => {
    // Check cache first
    const cached = getThemeCache(bookId);
    if (cached && cached.length > 0) {
      console.log(`Using cached themes for "${title}":`, cached);
      return cached;
    }
    
    console.log(`Scraping themes for "${title}" from backend API...`);
    
    try {
      // Call backend serverless function
      const params = new URLSearchParams({
        title,
        author,
        bookId: bookId || ''
      });
      
      const response = await fetch(`${THEMES_API}?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        console.error(`Theme API returned ${response.status} for "${title}"`);
        return [];
      }
      
      const data = await response.json();
      
      if (data.success && data.themes && data.themes.length > 0) {
        console.log(`Fetched ${data.themes.length} themes for "${title}":`, data.themes);
        
        // Cache results
        setThemeCache(bookId, data.themes);
        
        return data.themes;
      }
      
      return [];
    } catch (error) {
      console.error(`Error fetching themes for "${title}":`, error);
      return [];
    }
  };

  // ========== END WEB SCRAPING ==========

  // Toggle book like (marks as "read") - now just adds to read, sentiment is set separately
  const toggleBookLike = (book) => {
    // If already in want-to-read, remove from there
    if (wantToReadBooks.find(b => b.id === book.id)) {
      setWantToReadBooks(prev => prev.filter(b => b.id !== book.id));
    }
    // If already disliked, remove from disliked
    if (dislikedBooks.find(b => b.id === book.id)) {
      setDislikedBooks(prev => prev.filter(b => b.id !== book.id));
    }
    // Toggle like
    const isAlreadyRead = likedBooks.find(b => b.id === book.id);
    if (isAlreadyRead) {
      // Remove from liked and clear sentiment
      setLikedBooks(prev => prev.filter(b => b.id !== book.id));
      setBookSentiments(prev => {
        const newSentiments = { ...prev };
        delete newSentiments[book.id];
        return newSentiments;
      });
    } else {
      // Add to liked with default sentiment of 'liked'
      setLikedBooks(prev => [...prev, book]);
      setBookSentiments(prev => ({ ...prev, [book.id]: 'liked' }));
    }
  };

  // Set sentiment for a read book
  const setBookSentiment = (bookId, sentiment) => {
    setBookSentiments(prev => ({ ...prev, [bookId]: sentiment }));
  };

  // Add/remove book from my list (for recommendations)
  const toggleMyList = (book) => {
    setMyList(prev => {
      const targetKey = getBookKey(book);
      const exists = prev.find(b => getBookKey(b) === targetKey);
      if (exists) {
        return prev.filter(b => getBookKey(b) !== targetKey);
      }
      return [...prev, book];
    });
  };

  // Copy my list to clipboard
  const copyListToClipboard = () => {
    const listText = myList.map((book, index) => {
      const title = book.title;
      const author = book.author;
      const year = book.year;
      return `${index + 1}. "${title}" by ${author}${year ? ` (${year})` : ''}`;
    }).join('\n');
    
    const fullText = `My BiblioQuire Reading List\n${'='.repeat(30)}\n\n${listText}\n\nCreated with BiblioQuire - www.biblioquire.com`;

    const showCopied = () => {
      setListCopied(true);
      setTimeout(() => setListCopied(false), 2000);
    };

    navigator.clipboard.writeText(fullText).then(showCopied).catch(err => {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = fullText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      showCopied();
    });
  };

  // Toggle book as "want to read" (will show in results)
  const toggleBookWantToRead = (book) => {
    // If already liked, remove from liked
    if (likedBooks.find(b => b.id === book.id)) {
      setLikedBooks(prev => prev.filter(b => b.id !== book.id));
      setBookSentiments(prev => {
        const newSentiments = { ...prev };
        delete newSentiments[book.id];
        return newSentiments;
      });
    }
    // If already disliked, remove from disliked
    if (dislikedBooks.find(b => b.id === book.id)) {
      setDislikedBooks(prev => prev.filter(b => b.id !== book.id));
    }
    // Toggle want-to-read
    setWantToReadBooks(prev => 
      prev.find(b => b.id === book.id)
        ? prev.filter(b => b.id !== book.id)
        : [...prev, book]
    );
  };

  const toggleBookDislike = (book, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // If already liked, remove from liked and clear sentiment
    if (likedBooks.find(b => b.id === book.id)) {
      setLikedBooks(prev => prev.filter(b => b.id !== book.id));
      setBookSentiments(prev => {
        const newSentiments = { ...prev };
        delete newSentiments[book.id];
        return newSentiments;
      });
    }
    // If already in want-to-read, remove
    if (wantToReadBooks.find(b => b.id === book.id)) {
      setWantToReadBooks(prev => prev.filter(b => b.id !== book.id));
    }
    // Toggle dislike
    setDislikedBooks(prev => 
      prev.find(b => b.id === book.id)
        ? prev.filter(b => b.id !== book.id)
        : [...prev, book]
    );
  };

  // Generate a placeholder cover with title and author
  const generatePlaceholderCover = (title, author) => {
    // Create a canvas-based placeholder as data URL
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 192;
    const ctx = canvas.getContext('2d');
    
    // Background gradient based on title hash for variety
    const hash = title.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    const hue = hash % 360;
    ctx.fillStyle = `hsl(${hue}, 30%, 25%)`;
    ctx.fillRect(0, 0, 128, 192);
    
    // Add border
    ctx.strokeStyle = `hsl(${hue}, 40%, 40%)`;
    ctx.lineWidth = 4;
    ctx.strokeRect(6, 6, 116, 180);
    
    // Title text
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 12px Georgia, serif';
    ctx.textAlign = 'center';
    
    // Word wrap title
    const words = title.split(' ');
    let lines = [];
    let currentLine = '';
    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(testLine).width < 100) {
        currentLine = testLine;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);
    lines = lines.slice(0, 4); // Max 4 lines
    
    const startY = 50;
    lines.forEach((line, i) => {
      ctx.fillText(line, 64, startY + i * 16);
    });
    
    // Author text
    ctx.font = '10px Georgia, serif';
    ctx.fillStyle = '#cccccc';
    const authorDisplay = author.length > 20 ? author.slice(0, 18) + '...' : author;
    ctx.fillText(authorDisplay, 64, 160);
    
    return canvas.toDataURL('image/png');
  };

  // Test if an image URL actually loads a real cover (not a placeholder)
  const testImageUrl = (url) => {
    return new Promise((resolve) => {
      // Note: no crossOrigin attribute - cover hosts like books.google.com don't
      // send CORS headers, and we only need to know the image loads, not read it
      const img = new Image();

      const timeout = setTimeout(() => {
        resolve(false);
      }, 5000);
      
      img.onload = () => {
        clearTimeout(timeout);
        // Check if it's a real image with reasonable dimensions
        // Placeholder images are often very small or have suspicious aspect ratios
        if (img.naturalWidth > 50 && img.naturalHeight > 50) {
          resolve(true);
        } else {
          resolve(false);
        }
      };
      
      img.onerror = () => {
        clearTimeout(timeout);
        resolve(false);
      };
      
      img.src = url;
    });
  };

  // Fetch book cover from multiple sources with validation
  const fetchBookCover = async (title, author) => {
    // Collect all potential cover URLs from different sources
    const potentialCovers = [];
    
    // Try Google Books first
    try {
      const query = encodeURIComponent(`${title} ${author}`);
      const response = await fetch(`https://www.googleapis.com/books/v1/volumes?q=${query}&maxResults=5`);
      const data = await response.json();
      
      if (data.items) {
        for (const item of data.items) {
          const imageLinks = item.volumeInfo?.imageLinks;
          if (imageLinks) {
            // Prefer larger images
            const thumbnail = imageLinks.medium || imageLinks.large || imageLinks.thumbnail || imageLinks.smallThumbnail;
            if (thumbnail) {
              potentialCovers.push({
                url: thumbnail.replace('zoom=1', 'zoom=3').replace('http://', 'https://'),
                source: 'google'
              });
            }
          }
        }
      }
    } catch (err) {
      console.error('Google Books error for:', title, err);
    }
    
    // Try Open Library search
    try {
      const searchQuery = encodeURIComponent(`${title} ${author}`);
      const searchResponse = await fetch(`https://openlibrary.org/search.json?q=${searchQuery}&limit=5`);
      const searchData = await searchResponse.json();
      
      if (searchData.docs) {
        for (const doc of searchData.docs) {
          // Try cover_i first - use Large size
          if (doc.cover_i) {
            potentialCovers.push({
              url: `https://covers.openlibrary.org/b/id/${doc.cover_i}-L.jpg`,
              source: 'openlibrary-id'
            });
          }
          // Try ISBN-based cover
          if (doc.isbn && doc.isbn[0]) {
            potentialCovers.push({
              url: `https://covers.openlibrary.org/b/isbn/${doc.isbn[0]}-L.jpg`,
              source: 'openlibrary-isbn'
            });
          }
        }
      }
    } catch (err) {
      console.error('Open Library error for:', title, err);
    }
    
    // Try Google Books with just title
    try {
      const query = encodeURIComponent(title);
      const response = await fetch(`https://www.googleapis.com/books/v1/volumes?q=intitle:${query}&maxResults=3`);
      const data = await response.json();
      
      if (data.items) {
        for (const item of data.items) {
          const imageLinks = item.volumeInfo?.imageLinks;
          if (imageLinks) {
            const thumbnail = imageLinks.medium || imageLinks.large || imageLinks.thumbnail || imageLinks.smallThumbnail;
            if (thumbnail) {
              potentialCovers.push({
                url: thumbnail.replace('zoom=1', 'zoom=3').replace('http://', 'https://'),
                source: 'google-title'
              });
            }
          }
        }
      }
    } catch (err) {
      console.error('Google Books (title only) error for:', title, err);
    }
    
    // Now test each URL until we find one that works
    for (const cover of potentialCovers) {
      const isValid = await testImageUrl(cover.url);
      if (isValid) {
        console.log(`Found valid cover for "${title}" from ${cover.source}`);
        return cover.url;
      }
    }
    
    console.log(`No valid cover found for "${title}", using placeholder`);
    // Generate a placeholder cover as absolute last resort
    return generatePlaceholderCover(title, author);
  };

  const buildBiblioquireSearchUrl = (book) => {
    const searchTerm = [book.title, book.author].filter(Boolean).join(' ');
    return `${window.location.origin}/#search/${encodeURIComponent(searchTerm)}`;
  };

  const getBookKey = (book) => `${book.title}|${book.author || ''}`.toLowerCase().trim();

  // Fetch sample books for discovery step 2 - using curated database with dynamic theme scraping
  const fetchSampleBooks = async () => {
    setDiscoveryLoading(true);
    setScrapingProgress({ current: 0, total: 0 });
    
    try {
      // Get curated books from database (no theme filtering yet - we'll scrape themes first)
      let curatedBooks = getSampleBooks(
        selectedGenres,
        [], // No theme filtering yet
        [], // No exclusions yet
        200 // Large pool
      );
      
      // Shuffle for variety
      for (let i = curatedBooks.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [curatedBooks[i], curatedBooks[j]] = [curatedBooks[j], curatedBooks[i]];
      }

      // Normalize + validate
      curatedBooks = curatedBooks
        .map((book) => normalizeBookRecord(book))
        .filter((book) => isValidBookRecord(book));
      
      // Take 48 books to analyze
      const booksToAnalyze = curatedBooks.slice(0, 48);
      
      console.log(`Analyzing ${booksToAnalyze.length} books with web scraping...`);
      setScrapingProgress({ current: 0, total: booksToAnalyze.length });
      
      // Process books in batches of 3 to avoid rate limiting
      const BATCH_SIZE = 3;
      const analyzedBooks = [];
      
      for (let i = 0; i < booksToAnalyze.length; i += BATCH_SIZE) {
        const batch = booksToAnalyze.slice(i, Math.min(i + BATCH_SIZE, booksToAnalyze.length));
        
        const batchResults = await Promise.all(
          batch.map(async (book) => {
            // Fetch cover and themes in parallel
            const [coverUrl, scrapedThemes] = await Promise.all([
              fetchBookCover(book.title, book.author),
              fetchThemesMultiSource(book.title, book.author, book.id)
            ]);
            
            // Merge scraped themes with existing tags
            const existingTags = new Set(book.tags || []);
            scrapedThemes.forEach(theme => existingTags.add(theme));
            
            return normalizeBookRecord({
              ...book,
              tags: Array.from(existingTags),
              imageUrl: coverUrl || book.imageUrl || null,
              themesScraped: true
            });
          })
        );
        
        analyzedBooks.push(...batchResults);
        
        // Update progress
        setScrapingProgress({ current: analyzedBooks.length, total: booksToAnalyze.length });
        
        console.log(`Analyzed ${analyzedBooks.length}/${booksToAnalyze.length} books`);
      }
      
      console.log('All books analyzed with dynamic themes');
      
      // Set the books - no filtering needed since users will select from these
      setSampleBooks(analyzedBooks);
      
    } catch (err) {
      console.error('Error fetching sample books:', err);
    } finally {
      setDiscoveryLoading(false);
      setScrapingProgress({ current: 0, total: 0 });
    }
  };

  // Generate final recommendations - using AI + Google Books for metadata
  const generateRecommendations = async () => {
    setDiscoveryLoading(true);
    setLoadingMessageIndex(0);
    
    try {
      console.log('Generating AI-powered recommendations...');
      console.log('Loved books:', likedBooks.filter(b => bookSentiments[b.id] === 'loved').map(b => b.title));
      console.log('Liked books:', likedBooks.filter(b => bookSentiments[b.id] !== 'loved').map(b => b.title));
      console.log('Want to read:', wantToReadBooks.map(b => b.title));

      // Separate loved from liked books
      const lovedBooks = likedBooks.filter(b => bookSentiments[b.id] === 'loved');
      const justLikedBooks = likedBooks.filter(b => bookSentiments[b.id] !== 'loved');

      // Get all titles to exclude (sample books + interacted books)
      const excludeTitles = [
        ...sampleBooks.map(b => b.title),
        ...likedBooks.map(b => b.title),
        ...dislikedBooks.map(b => b.title),
        ...wantToReadBooks.map(b => b.title),
      ];

      // Get genre names
      const genreNames = selectedGenres.map(id => getDiscoveryGenreName(id)).filter(Boolean);

      // Call our AI recommendation API
      let aiRecommendations = [];
      try {
        const response = await fetch(RECOMMEND_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lovedBooks: lovedBooks.map(b => ({ title: b.title, author: b.author })),
            likedBooks: justLikedBooks.map(b => ({ title: b.title, author: b.author })),
            wantToReadBooks: wantToReadBooks.map(b => ({ title: b.title, author: b.author })),
            genres: genreNames,
            excludeTitles: excludeTitles,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          aiRecommendations = data.recommendations || [];
          console.log('AI recommendations received:', aiRecommendations.length);
        } else {
          console.error('AI API error:', await response.text());
        }
      } catch (err) {
        console.error('Error calling AI recommendation API:', err);
      }

      // Now fetch book metadata from Google Books for each AI recommendation
      const finalRecommendations = [];
      const seenTitles = new Set(excludeTitles.map(t => t.toLowerCase().trim()));

      for (const rec of aiRecommendations.slice(0, 20)) {
        try {
          // Search Google Books for this specific book
          const query = `"${rec.title}" ${rec.author}`;
          const url = `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(query)}&maxResults=3`;
          const response = await fetch(url);
          
          if (!response.ok) continue;
          
          const data = await response.json();
          const items = data.items || [];
          
          // Find the best match
          for (const item of items) {
            const info = item.volumeInfo || {};
            const title = info.title || '';
            const authors = info.authors || [];
            
            // Check if this is the right book (fuzzy match)
            const titleMatch = title.toLowerCase().includes(rec.title.toLowerCase().slice(0, 20)) ||
                              rec.title.toLowerCase().includes(title.toLowerCase().slice(0, 20));
            
            if (!titleMatch) continue;
            if (seenTitles.has(title.toLowerCase().trim())) continue;
            
            seenTitles.add(title.toLowerCase().trim());
            
            const description = info.description || '';
            const year = info.publishedDate ? parseInt(info.publishedDate.substring(0, 4)) : null;
            const rawImageUrl = info.imageLinks?.thumbnail || info.imageLinks?.smallThumbnail || null;
            const imageUrl = rawImageUrl ? rawImageUrl.replace('http://', 'https://') : null;
            
            const book = normalizeBookRecord({
              id: `ai-${item.id}`,
              title: title,
              author: authors.join(', '),
              description: description,
              year: year,
              tags: info.categories || [],
              source: 'ai-gemini',
              imageUrl: imageUrl,
              aiReason: rec.reason, // Store the AI's reason for this recommendation
            });
            
            if (isValidBookRecord(book)) {
              finalRecommendations.push(book);
              break; // Found a good match, move to next AI recommendation
            }
          }
        } catch (err) {
          console.error(`Error fetching metadata for "${rec.title}":`, err);
        }
      }

      console.log('Final AI recommendations with metadata:', finalRecommendations.length);

      // Fallback: if the AI service is unavailable or nothing matched, build
      // recommendations from the curated database so users always get results
      if (finalRecommendations.length === 0) {
        console.log('Using curated fallback recommendations');
        const seedBooks = [
          ...likedBooks.filter(b => bookSentiments[b.id] !== 'meh'),
          ...wantToReadBooks,
        ];
        const fallback = getRecommendations(seedBooks, selectedGenres, [], [], 20)
          .filter(b => !seenTitles.has((b.title || '').toLowerCase().trim()))
          .map(b => normalizeBookRecord({ ...b, source: 'curated' }))
          .filter(b => isValidBookRecord(b));
        finalRecommendations.push(...fallback);
      }

      // Categorize recommendations
      const categorizedRecommendations = {
        wantToRead: wantToReadBooks.map(book => normalizeBookRecord(book)),
        recommended: finalRecommendations,
      };

      setRecommendations(categorizedRecommendations);
      setDiscoveryStep(3);
    } catch (err) {
      console.error('Error generating recommendations:', err);
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const nextDiscoveryStep = async () => {
    // Scroll to the top of the step box content
    setTimeout(() => {
      const stepBox = document.querySelector('.discovery-step');
      if (stepBox) {
        const yOffset = -20; // Small offset from top
        const y = stepBox.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
    
    if (discoveryStep === 1 && selectedGenres.length > 0) {
      setDiscoveryStep(2);
      await fetchSampleBooks();
    } else if (discoveryStep === 2) {
      await generateRecommendations();
    }
  };

  const prevDiscoveryStep = () => {
    // Scroll to the top of the step box content
    setTimeout(() => {
      const stepBox = document.querySelector('.discovery-step');
      if (stepBox) {
        const yOffset = -20; // Small offset from top
        const y = stepBox.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
    
    if (discoveryStep > 1) {
      setDiscoveryStep(discoveryStep - 1);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const saveWant = () => {
    if (!wantName.trim()) return;
    setWants((prev) => Array.from(new Set([...prev, wantName.trim()])));
    setWantName('');
  };

  return (
    <div className="page">
      <header>
        <div className="header-top">
          <div className="logo" onClick={goHome} style={{ cursor: 'pointer' }}>
            <div className="logo-icon">BQ</div>
            <div>
              <div className="logo-text">BiblioQuire</div>
              <div className="logo-tagline">Purveyors of Rare &amp; Collectible Books</div>
            </div>
          </div>
          <nav>
            <a href="#home" onClick={(e) => { e.preventDefault(); goHome(); }} className={view === 'home' ? 'active' : ''}>Home</a>
            <a href="#browse" onClick={(e) => { e.preventDefault(); setView('browse'); setSelectedCategory(''); }} className={view === 'browse' ? 'active' : ''}>Browse</a>
            <a href="#search" onClick={(e) => { e.preventDefault(); setView('search'); }} className={view === 'search' ? 'active' : ''}>Search</a>
            <a href="#discover" onClick={(e) => { e.preventDefault(); startDiscovery(); }} className={view === 'discover' ? 'active' : ''}>Discover</a>
            <a href="#about" onClick={(e) => { e.preventDefault(); setView('about'); }} className={view === 'about' ? 'active' : ''}>About</a>
            <button className="ghost coming-soon" title="Coming Soon">Sign Up</button>
            <button className="ghost coming-soon" title="Coming Soon">Log In</button>
            <button 
              className="theme-toggle" 
              onClick={toggleTheme} 
              title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              aria-label="Toggle theme"
            >
              {getThemeIcon()}
            </button>
          </nav>
        </div>

        <div className="search-section">
          <div className="search-container">
            <div className="search-title">Search Books</div>
            <div className="search-box">
              <div className="search-input-wrapper">
                <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  type="text"
                  className="search-input"
                  placeholder="Title, author, keyword, ISBN..."
                />
              </div>
              <button className="search-btn" onClick={handleSearch}>Search</button>
            </div>

            <div className="advanced-toggle" onClick={() => setShowAdvanced((v) => !v)}>
              <span>Advanced Search Options</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={showAdvanced ? 'rotated' : ''}>                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>

            {showAdvanced && (
              <div className="advanced-search">
                {/* Row 1: Author & Title */}
                <div className="advanced-row">
                  <div className="advanced-field">
                    <label className="field-label">Author</label>
                    <input
                      type="text"
                      value={authorField}
                      onChange={(e) => setAuthorField(e.target.value)}
                      className="field-input"
                      placeholder="e.g., Hemingway"
                    />
                  </div>
                  <div className="advanced-field">
                    <label className="field-label">Title</label>
                    <input
                      type="text"
                      value={titleField}
                      onChange={(e) => setTitleField(e.target.value)}
                      className="field-input"
                      placeholder="e.g., The Old Man and the Sea"
                    />
                  </div>
                </div>

                {/* Row 2: Keywords - simple comma-separated */}
                <div className="advanced-field full-width">
                  <label className="field-label">Keywords <span className="field-hint">(separate with commas)</span></label>
                  <input
                    type="text"
                    value={keywordsField}
                    onChange={(e) => setKeywordsField(e.target.value)}
                    className="field-input"
                    placeholder="e.g., first edition, signed, leather bound"
                  />
                </div>

                {/* Row 3: ISBN & Publisher */}
                <div className="advanced-row">
                  <div className="advanced-field">
                    <label className="field-label">ISBN</label>
                    <input
                      type="text"
                      value={isbnField}
                      onChange={(e) => setIsbnField(e.target.value)}
                      className="field-input"
                      placeholder="e.g., 978-0-06-112008-4"
                    />
                  </div>
                  <div className="advanced-field">
                    <label className="field-label">Publisher</label>
                    <input
                      type="text"
                      value={publisherField}
                      onChange={(e) => setPublisherField(e.target.value)}
                      className="field-input"
                      placeholder="e.g., Scribner"
                    />
                  </div>
                </div>

                {/* Row 4: Year Range & Price Range */}
                <div className="advanced-row">
                  <div className="advanced-field">
                    <label className="field-label">Year</label>
                    <div className="range-inputs">
                      <input
                        type="number"
                        value={yearFrom}
                        onChange={(e) => setYearFrom(e.target.value)}
                        className="field-input range-input"
                        placeholder="From"
                        min="1400"
                        max="2030"
                      />
                      <span className="range-separator">to</span>
                      <input
                        type="number"
                        value={yearTo}
                        onChange={(e) => setYearTo(e.target.value)}
                        className="field-input range-input"
                        placeholder="To"
                        min="1400"
                        max="2030"
                      />
                    </div>
                  </div>
                  <div className="advanced-field">
                    <label className="field-label">Price Range (USD)</label>
                    <div className="range-inputs">
                      <input
                        type="number"
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                        className="field-input range-input"
                        placeholder="$ Min"
                        min="0"
                      />
                      <span className="range-separator">to</span>
                      <input
                        type="number"
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                        className="field-input range-input"
                        placeholder="$ Max"
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Row 5: Condition */}
                <div className="advanced-row">
                  <div className="advanced-field">
                    <label className="field-label">Condition</label>
                    <select 
                      value={conditionFilter} 
                      onChange={(e) => setConditionFilter(e.target.value)}
                      className="field-select"
                    >
                      <option value="">Any Condition</option>
                      <option value="new">New</option>
                      <option value="like-new">Like New / Very Good</option>
                      <option value="good">Good</option>
                      <option value="acceptable">Acceptable / Fair</option>
                    </select>
                  </div>
                </div>

                {/* Filters Checkboxes */}
                <div className="filters-section">
                  <label className="field-label">Filters</label>
                  <div className="filters-grid">
                    <label className="filter-checkbox">
                      <input type="checkbox" checked={firstEdition} onChange={(e) => setFirstEdition(e.target.checked)} />
                      <span>First Edition</span>
                    </label>
                    <label className="filter-checkbox">
                      <input type="checkbox" checked={signedCopy} onChange={(e) => setSignedCopy(e.target.checked)} />
                      <span>Signed</span>
                    </label>
                    <label className="filter-checkbox">
                      <input type="checkbox" checked={dustJacket} onChange={(e) => setDustJacket(e.target.checked)} />
                      <span>Dust Jacket</span>
                    </label>
                    <label className="filter-checkbox">
                      <input type="checkbox" checked={imageOnly} onChange={(e) => setImageOnly(e.target.checked)} />
                      <span>With Images Only</span>
                    </label>
                    <label className="filter-checkbox">
                      <input type="checkbox" checked={excludePaperback} onChange={(e) => setExcludePaperback(e.target.checked)} />
                      <span>Exclude Paperbacks</span>
                    </label>
                    <label className="filter-checkbox">
                      <input type="checkbox" checked={excludeExLibrary} onChange={(e) => setExcludeExLibrary(e.target.checked)} />
                      <span>Exclude Ex-Library</span>
                    </label>
                    <label className="filter-checkbox">
                      <input type="checkbox" checked={excludeBookClub} onChange={(e) => setExcludeBookClub(e.target.checked)} />
                      <span>Exclude Book Club</span>
                    </label>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="advanced-actions">
                  <button type="button" className="reset-btn" onClick={resetAdvancedSearch}>
                    Reset Form
                  </button>
                  <button type="button" className="search-advanced-btn" onClick={handleSearch}>
                    Search Books
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <main>
        {/* HOME VIEW */}
        {view === 'home' && (
          <>
            {/* Browse by Genre */}
            <section className="genre-section">
              <div className="section-header">
                <h2 className="section-title">Browse by Genre</h2>
              </div>
              {/* Mobile: Dropdown selector */}
              <div className="genre-dropdown-mobile">
                <select 
                  className="genre-select"
                  value=""
                  onChange={(e) => e.target.value && handleGenreClick(e.target.value)}
                >
                  <option value="">Select a genre...</option>
                  {GENRES.map((genre) => (
                    <option key={genre.id} value={genre.id}>
                      {genre.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* Desktop: Grid of genre cards */}
              <div className="genre-grid genre-grid-desktop">
                {GENRES.map((genre) => (
                  <GenreCard key={genre.id} genre={genre} onClick={handleGenreClick} />
                ))}
              </div>
            </section>

            {/* Discovery Tool Promo */}
            <section className="discovery-promo">
              <div className="discovery-promo-content">
                <div className="discovery-promo-text">
                  <h2>Not Sure What to Read Next?</h2>
                  <p>Let us help you discover your perfect book based on your tastes and preferences.</p>
                  <button className="discovery-promo-btn" onClick={startDiscovery}>
                    <WandSparkles aria-hidden="true" /> Find Your Next Book
                  </button>
                </div>
                <div className="discovery-promo-icons">
                  {renderIcon(BookOpenText, 'promo-icon')}
                  {renderIcon(Sparkles, 'promo-icon')}
                  {renderIcon(Target, 'promo-icon')}
                </div>
              </div>
            </section>

            {/* Genre Carousel Sections */}
            {HOMEPAGE_GENRES.map((genre) => (
              <section key={genre.id} className="genre-carousel-section">
                <div className="section-header">
                  <div>
                    <h2 className="section-title">
                      {renderIcon(genre.icon, 'genre-icon-inline')} {genre.name}
                    </h2>
                    <p className="section-subtitle">First editions, vintage & collectible {genre.name.toLowerCase()} for sale</p>
                  </div>
                  <button className="view-all" onClick={() => {
                    setSelectedCategory(genre.id);
                    setBrowseAllMode(true);
                    setSelectedTitleGroup(null);
                    setSelectedTitleAuthor(null);
                    setTitleListings([]);
                    setTitleOffset(0);
                    setTitleHasMore(true);
                    setBrowseRefineOptions({ firstEdition: false, signed: false });
                    setView('browse');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}>
                    View All →
                  </button>
                </div>
                {homepageLoading[genre.id] ? (
                  <div className="carousel-loading">
                    <div className="spinner small"></div>
                    <span>Loading {genre.name.toLowerCase()}…</span>
                  </div>
                ) : (homepageGenres[genre.id]?.length > 0) ? (
                  <div className="genre-carousel-wrapper">
                    <div className="genre-carousel">
                      {homepageGenres[genre.id].map((listing) => (
                        <div key={listing.id} className="carousel-item">
                          <ListingCard listing={listing} onClick={handleCardClick} />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="empty-carousel">No {genre.name.toLowerCase()} books available right now.</div>
                )}
              </section>
            ))}
          </>
        )}

        {/* BROWSE VIEW */}
        {view === 'browse' && (
          <section className="featured-section">
            <div className="section-header">
              <div>
                <h2 className="section-title">
                  {browseAllMode
                    ? `All ${GENRES.find(g => g.id === selectedCategory)?.name || selectedCategory} Books`
                    : selectedTitleGroup 
                      ? `${selectedTitleGroup}`
                      : selectedCategory 
                        ? `Explore ${GENRES.find(g => g.id === selectedCategory)?.name || selectedCategory}`
                        : 'Select a Genre'}
                </h2>
                <p className="section-subtitle">
                  {browseAllMode
                    ? `${listings.length} listings found on eBay`
                    : selectedTitleGroup
                      ? 'Browse available listings'
                      : selectedCategory 
                        ? `${[...new Set(listings.map(l => l.matchedTitle).filter(Boolean))].length} popular series`
                        : 'Choose a category to explore popular titles'}
                </p>
              </div>
              <div className="header-controls">
                {browseAllMode ? (
                  <button className="back-to-groups-btn" onClick={() => { setSelectedCategory(''); setSelectedTitleGroup(null); setBrowseAllMode(false); }}>
                    ← Back to Genres
                  </button>
                ) : selectedTitleGroup ? (
                  <button className="back-to-groups-btn" onClick={() => { 
                    setSelectedTitleGroup(null); 
                    setSelectedTitleAuthor(null); 
                    setTitleListings([]); 
                    setTitleOffset(0);
                    setTitleHasMore(true);
                    setBrowseRefineOptions({ firstEdition: false, signed: false });
                  }}>
                    ← Back to {GENRES.find(g => g.id === selectedCategory)?.name || 'titles'}
                  </button>
                ) : selectedCategory && (
                  <button className="back-to-groups-btn" onClick={() => { setSelectedCategory(''); setSelectedTitleGroup(null); setBrowseAllMode(false); }}>
                    ← Back to Genres
                  </button>
                )}
              </div>
            </div>



            {!selectedCategory ? (
              <>
                {/* Mobile: Dropdown selector */}
                <div className="genre-dropdown-mobile">
                  <select 
                    className="genre-select"
                    value=""
                    onChange={(e) => e.target.value && handleGenreClick(e.target.value)}
                  >
                    <option value="">Select a genre...</option>
                    {GENRES.map((genre) => (
                      <option key={genre.id} value={genre.id}>
                        {genre.name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Desktop: Grid of genre cards */}
                <div className="genre-grid genre-grid-desktop">
                  {GENRES.map((genre) => (
                    <GenreCard key={genre.id} genre={genre} onClick={handleGenreClick} />
                  ))}
                </div>
              </>
            ) : loading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <span>{browseAllMode ? 'Searching eBay…' : `Finding popular ${GENRES.find(g => g.id === selectedCategory)?.name.toLowerCase() || selectedCategory} titles…`}</span>
              </div>
            ) : listings.length > 0 ? (
              <>
                {/* Browse All Mode: show regular listings grid */}
                {browseAllMode ? (
                  <div className="featured-grid">
                    {listings.map((listing) => (
                      <ListingCard key={listing.id} listing={listing} onClick={handleCardClick} onFindEditions={openEditionPicker} />
                    ))}
                  </div>
                ) : !selectedTitleGroup ? (
                  /* Show grouped title cards when no specific title is selected */
                  <>
                    {/* View All option */}
                    <div className="browse-options">
                      <button 
                        className="view-all-listings-btn"
                        onClick={() => { setBrowseAllMode(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      >
                        Browse All {GENRES.find(g => g.id === selectedCategory)?.name || selectedCategory} Books
                      </button>
                      <p className="browse-hint">Or select a popular series below</p>
                    </div>
                    <div className="title-groups-grid">
                      {(() => {
                        // Group listings by matchedTitle, keeping author info
                        const groups = {};
                        listings.forEach(listing => {
                          const title = listing.matchedTitle || 'Other';
                          if (!groups[title]) {
                            groups[title] = {
                              items: [],
                              author: listing.matchedAuthor || null
                            };
                          }
                          groups[title].items.push(listing);
                        });
                        
                        // Sort groups alphabetically by title
                        return Object.entries(groups)
                          .sort((a, b) => a[0].localeCompare(b[0]))
                          .map(([title, data]) => (
                            <div
                              key={title}
                              className="title-group-card"
                              role="button"
                              tabIndex={0}
                              onClick={() => handleTitleGroupClick(title, data.author)}
                              onKeyDown={cardKeyHandler(() => handleTitleGroupClick(title, data.author))}
                            >
                              <div className="title-group-images">
                                {data.items.slice(0, 3).map((item, idx) => (
                                  <img 
                                    key={idx} 
                                    src={item.imageUrl || item.image || '/placeholder-book.png'} 
                                    alt="" 
                                    className="title-group-thumb"
                                    style={{ zIndex: 3 - idx }}
                                  />
                                ))}
                              </div>
                              <h3 className="title-group-name">{title}</h3>
                              <p className="title-group-price">
                                From ${Math.min(...data.items.map(i => i.price || 9999)).toFixed(2)}
                              </p>
                            </div>
                          ));
                      })()}
                    </div>
                  </>
                ) : (
                  /* Show listings for selected title group - with loading state and direct fetch */
                  titleLoading ? (
                    <div className="loading-spinner">
                      <div className="spinner"></div>
                      <span>Finding {selectedTitleGroup} listings…</span>
                    </div>
                  ) : titleListings.length > 0 ? (
                    <>
                      {/* Refine Dropdown - shown when viewing a specific series */}
                      <div className="browse-refine-section">
                        <label className="refine-label">Refine your search for {selectedTitleGroup}:</label>
                        <div className="refine-options">
                          <label className="refine-checkbox">
                            <input 
                              type="checkbox" 
                              checked={browseRefineOptions.firstEdition}
                              onChange={(e) => {
                                setBrowseRefineOptions(prev => ({ ...prev, firstEdition: e.target.checked }));
                              }}
                            />
                            <span>First Edition</span>
                          </label>
                          <label className="refine-checkbox">
                            <input 
                              type="checkbox" 
                              checked={browseRefineOptions.signed}
                              onChange={(e) => {
                                setBrowseRefineOptions(prev => ({ ...prev, signed: e.target.checked }));
                              }}
                            />
                            <span>Signed Copy</span>
                          </label>
                          <button 
                            className="refine-apply-btn"
                            onClick={async () => {
                              setTitleLoading(true);
                              setTitleListings([]);
                              setTitleOffset(0);
                              setTitleHasMore(true);
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                              
                              try {
                                // Build search term with title/author and filter keywords
                                let searchTerm = selectedTitleAuthor 
                                  ? `${selectedTitleGroup} ${selectedTitleAuthor}` 
                                  : selectedTitleGroup;
                                
                                // Add filter keywords to search query
                                if (browseRefineOptions.firstEdition) {
                                  searchTerm += ' first edition';
                                }
                                if (browseRefineOptions.signed) {
                                  searchTerm += ' signed';
                                }
                                
                                const endpoint = LISTINGS_API;
                                const params = new URLSearchParams({ 
                                  q: searchTerm, 
                                  limit: '48', 
                                  offset: '0',
                                  sort: 'newest' 
                                });
                                
                                const res = await fetch(`${endpoint}?${params.toString()}`);
                                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                                const data = await res.json();
                                
                                const results = data.items || [];
                                setTitleListings(results);
                                setTitleHasMore(results.length >= 48);
                              } catch (err) {
                                console.error('Error fetching refined title listings:', err);
                                setTitleListings([]);
                                setTitleHasMore(false);
                              } finally {
                                setTitleLoading(false);
                              }
                            }}
                          >
                            Apply Filters
                          </button>
                        </div>
                      </div>
                      <div className="featured-grid">
                        {titleListings.map((listing) => (
                          <ListingCard key={listing.id} listing={listing} onClick={handleCardClick} onFindEditions={openEditionPicker} />
                        ))}
                      </div>
                      {titleLoading && (
                        <div className="loading-more">
                          <div className="spinner small"></div>
                          <span>Loading more listings…</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="empty">No listings found for {selectedTitleGroup}.</div>
                  )
                )}
              </>
            ) : (
              <div className="empty">No books found in this category.</div>
            )}
          </section>
        )}

        {/* SEARCH VIEW */}
        {view === 'search' && (
          <section className="featured-section">
            <div className="section-header">
              <div>
                <h2 className="section-title">
                  {selectedTitleGroup ? `Listings for "${selectedTitleGroup}"` : searchQuery ? `Results for "${searchQuery}"` : 'Search Results'}
                </h2>
                <p className="section-subtitle">
                  {selectedTitleGroup 
                    ? `${listings.filter(l => l.matchedTitle === selectedTitleGroup).length} listings found`
                    : listings.length > 0 
                      ? `Found ${listings.length} books for sale${isGroupedResults ? ` across ${[...new Set(listings.map(l => l.matchedTitle).filter(Boolean))].length} titles` : ''}`
                      : 'Enter a search term above'}
                </p>
              </div>
              <div className="header-controls">
                {selectedTitleGroup && (
                  <button className="back-to-groups-btn" onClick={() => setSelectedTitleGroup(null)}>
                    ← Back to all titles
                  </button>
                )}
                {listings.length > 1 && !isGroupedResults && (
                  <div className="sort-controls">
                    <label htmlFor="sort">Sort by:</label>
                    <select 
                      id="sort" 
                      value={sortBy} 
                      onChange={(e) => setSortBy(e.target.value)}
                      className="sort-select"
                    >
                      <option value="relevance">Relevance</option>
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="title">Title A-Z</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            {loading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <span>{popularOnly ? 'Searching popular titles…' : 'Searching across book sources…'}</span>
              </div>
            ) : listings.length > 0 ? (
              <>
                {/* Show grouped title cards when results are grouped and no specific title is selected */}
                {isGroupedResults && !selectedTitleGroup ? (
                  <div className="title-groups-grid">
                    {(() => {
                      // Group listings by matchedTitle
                      const groups = {};
                      listings.forEach(listing => {
                        const title = listing.matchedTitle || 'Other';
                        if (!groups[title]) groups[title] = [];
                        groups[title].push(listing);
                      });
                      
                      // Sort groups by listing count (most first) and return cards
                      return Object.entries(groups)
                        .sort((a, b) => b[1].length - a[1].length)
                        .map(([title, items]) => (
                          <div
                            key={title}
                            className="title-group-card"
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedTitleGroup(title)}
                            onKeyDown={cardKeyHandler(() => setSelectedTitleGroup(title))}
                          >
                            <div className="title-group-images">
                              {items.slice(0, 3).map((item, idx) => (
                                <img 
                                  key={idx} 
                                  src={item.imageUrl || item.image || '/placeholder-book.png'} 
                                  alt="" 
                                  className="title-group-thumb"
                                  style={{ zIndex: 3 - idx }}
                                />
                              ))}
                            </div>
                            <h3 className="title-group-name">{title}</h3>
                            <p className="title-group-count">{items.length} listing{items.length !== 1 ? 's' : ''}</p>
                            <p className="title-group-price">
                              From ${Math.min(...items.map(i => i.price || 9999)).toFixed(2)}
                            </p>
                          </div>
                        ));
                    })()}
                  </div>
                ) : (
                  <div className="featured-grid">
                    {[...(selectedTitleGroup ? listings.filter(l => l.matchedTitle === selectedTitleGroup) : listings)]
                      .sort((a, b) => {
                        if (sortBy === 'price-low') return (a.price || 9999) - (b.price || 9999);
                        if (sortBy === 'price-high') return (b.price || 0) - (a.price || 0);
                        if (sortBy === 'title') return (a.title || '').localeCompare(b.title || '');
                        return 0; // relevance - keep original order
                      })
                      .map((listing) => (
                      <ListingCard key={listing.id} listing={listing} onClick={handleCardClick} onFindEditions={openEditionPicker} />
                    ))}
                  </div>
                )}
                {loadingMore && (
                  <div className="loading-more">
                    <div className="spinner small"></div>
                    <span>Loading more books…</span>
                  </div>
                )}
                {!hasMore && listings.length >= ITEMS_PER_PAGE && !isGroupedResults && (
                  <div className="end-of-results">You've seen all available results</div>
                )}
              </>
            ) : searchQuery ? (
              <div className="empty">
                <p>No books currently for sale matching "{searchQuery}".</p>
                <p className="empty-hint">We only show books that are available for purchase. Try different keywords or check back later.</p>
              </div>
            ) : (
              <div className="empty">Enter a search term to find books for sale.</div>
            )}
          </section>
        )}

        {/* EDITION PICKER MODAL */}
        {showEditionPicker && editionPickerBook && (
          <div className="edition-picker-modal">
            <div className="edition-picker-overlay" onClick={closeEditionPicker}></div>
            <div className="edition-picker-content">
              <button className="edition-picker-close" onClick={closeEditionPicker}>×</button>
              <div className="edition-picker-header">
                <h2>Find a Specific Edition</h2>
                <p className="edition-subtitle">Searching for: <strong>{editionPickerBook.title}</strong></p>
              </div>
              
              {editionLoading ? (
                <div className="edition-loading">
                  <div className="spinner"></div>
                  <p>Finding editions...</p>
                </div>
              ) : (
                <div className="editions-grid">
                  {editionResults.map((book) => (
                    <div
                      key={book.id}
                      className="edition-card"
                      onClick={() => selectEdition(book)}
                    >
                      <div className="edition-cover">
                        <BookCover src={book.imageUrl} title={book.title} author={book.author} />
                      </div>
                      <div className="edition-info">
                        <p className="edition-title">{book.title}</p>
                        {book.author && <p className="edition-author">{book.author}</p>}
                        {book.year && <p className="edition-year">{book.year}</p>}
                        {book.price && <p className="edition-price">${book.price.toFixed(2)}</p>}
                        {book.condition && <p className="edition-condition">{book.condition}</p>}
                        <span className="edition-source">{book.source}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {!editionLoading && editionResults.length === 0 && (
                <div className="no-editions">No other editions found for this title.</div>
              )}
            </div>
          </div>
        )}

        {/* DISCOVERY VIEW */}
        {view === 'discover' && (
          <section className="discovery-section">
            <div className="discovery-header">
              <h1 className="discovery-title">Find Your Next Book</h1>
              <p className="discovery-subtitle">Let us help you discover your perfect read</p>
              
              {/* Back button at top */}
              {discoveryStep > 1 && (
                <button className="discovery-top-back" onClick={prevDiscoveryStep}>
                  ← Back to {DISCOVERY_STEPS[discoveryStep - 2]?.name || 'Previous'}
                </button>
              )}
              
              {/* Clickable progress steps */}
              <div className="discovery-progress">
                {DISCOVERY_STEPS.map((step, index) => (
                  <React.Fragment key={step.num}>
                    {index > 0 && <div className={`progress-line ${discoveryStep > index ? 'completed' : ''}`}></div>}
                    <div 
                      className={`progress-step ${discoveryStep >= step.num ? 'active' : ''} ${discoveryStep > step.num ? 'completed' : ''} ${isStepAccessible(step.num) ? 'clickable' : ''}`}
                      onClick={() => isStepAccessible(step.num) && step.num < discoveryStep && goToDiscoveryStep(step.num)}
                      title={isStepAccessible(step.num) && step.num < discoveryStep ? `Go back to ${step.name}` : ''}
                    >
                      <span className="step-number">{discoveryStep > step.num ? '✓' : step.num}</span>
                      <span className="step-label">{step.name}</span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Step 1: Choose Genres */}
            {discoveryStep === 1 && (
              <div className="discovery-step">
                <h2>What genres do you enjoy?</h2>
                <p className="step-hint">Select up to 5 genres (at least 1)</p>
                <div className="selection-grid">
                  {DISCOVERY_GENRES.map(genre => (
                    <button
                      key={genre.id}
                      className={`selection-card ${selectedGenres.includes(genre.id) ? 'selected' : ''}`}
                      onClick={() => toggleGenreSelection(genre.id)}
                    >
                      {renderIcon(genre.icon, 'selection-icon')}
                      <span className="selection-name">{genre.name}</span>
                    </button>
                  ))}
                </div>
                {selectedGenres.length > 0 && (
                  <div className="selection-summary">
                    Selected: {selectedGenres.map(id => getDiscoveryGenreName(id)).join(', ')}
                  </div>
                )}
                <div className="discovery-actions">
                  <button 
                    className="discovery-next-btn"
                    onClick={nextDiscoveryStep}
                    disabled={selectedGenres.length === 0}
                  >
                    Continue →
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Pick Books - Read/Want to Read/Not Interested */}
            {discoveryStep === 2 && (
              <div className="discovery-step">
                <h2>Pick books that interest you</h2>
                <p className="step-hint">Mark books you've read and rate them, or add books to your "want to read" list</p>
                
                {/* Loading/Analysis message */}
                {discoveryLoading && scrapingProgress.total > 0 && (
                  <div className="discovery-tip-box scraping-progress">
                    <span className="tip-icon">🔍</span>
                    <div className="tip-content">
                      <strong>Analyzing book themes from multiple web sources...</strong>
                      <div className="progress-bar-container">
                        <div className="progress-bar" style={{ width: `${(scrapingProgress.current / scrapingProgress.total) * 100}%` }}></div>
                      </div>
                      <p>Analyzed {scrapingProgress.current} of {scrapingProgress.total} books • This takes about a minute ☕</p>
                    </div>
                  </div>
                )}
                
                {/* Instructional tip box */}
                {!discoveryLoading && (
                  <div className="discovery-tip-box">
                    <span className="tip-icon">💡</span>
                    <div className="tip-content">
                      <strong>Pro tip:</strong> For best results, mark at least 3-5 books you've read and rate how much you enjoyed them. 
                      Books you skip will be automatically excluded from recommendations.
                    </div>
                  </div>
                )}
                
                {discoveryLoading && scrapingProgress.total === 0 ? (
                  <div className="loading-spinner">
                    <div className="spinner"></div>
                    <span>{LOADING_MESSAGES[loadingMessageIndex]}</span>
                  </div>
                ) : (sampleBooks.length > 0 || discoveryLoading) ? (
                  <>
                    <div className="sample-books-grid curated">
                      {sampleBooks.map(book => {
                        const isRead = likedBooks.find(b => b.id === book.id);
                        const isWantToRead = wantToReadBooks.find(b => b.id === book.id);
                        const isNotInterested = dislikedBooks.find(b => b.id === book.id);
                        const sentiment = bookSentiments[book.id];
                        const themeTags = (book.coreThemes && book.coreThemes.length > 0) ? book.coreThemes : (book.tags || []);
                        return (
                          <div
                            key={book.id}
                            className={`sample-book-card curated ${isRead ? 'read' : ''} ${isWantToRead ? 'want-to-read' : ''} ${isNotInterested ? 'disliked' : ''} ${sentiment ? `sentiment-${sentiment}` : ''}`}
                          >
                            <div className="curated-book-cover">
                              <BookCover src={book.imageUrl} title={book.title} author={book.author} />
                              {isRead && (
                                <div className={`book-status-badge read-badge ${sentiment || 'liked'}`}>
                                  {sentiment === 'loved' ? (
                                    <>
                                      <Heart aria-hidden="true" /> Loved
                                    </>
                                  ) : sentiment === 'neutral' ? (
                                    <>
                                      <Minus aria-hidden="true" /> Okay
                                    </>
                                  ) : sentiment === 'meh' ? (
                                    <>
                                      <ThumbsDown aria-hidden="true" /> Meh
                                    </>
                                  ) : (
                                    <>
                                      <ThumbsUp aria-hidden="true" /> Liked
                                    </>
                                  )}
                                </div>
                              )}
                              {isWantToRead && (
                                <div className="book-status-badge want-badge">
                                  <BookMarked aria-hidden="true" /> Want!
                                </div>
                              )}
                              {isNotInterested && (
                                <div className="book-status-badge not-interested-badge">
                                  <X aria-hidden="true" />
                                </div>
                              )}
                            </div>
                            <div className="curated-book-info">
                              <h4 className="curated-book-title">{book.title}</h4>
                              <p className="curated-book-author">by {book.author}</p>
                              {book.year && <p className="curated-book-year">{book.year}</p>}
                              {(book.shortDescription || book.description) && (
                                <div className="curated-book-description">
                                  <p>
                                    {expandedDescriptions[book.id] 
                                      ? (book.description || book.shortDescription)
                                      : book.shortDescription}
                                  </p>
                                  {book.description && book.description.length > 160 && (
                                    <button 
                                      className="see-more-btn"
                                      onClick={(e) => toggleDescriptionExpand(book.id, e)}
                                    >
                                      {expandedDescriptions[book.id] ? 'See less' : 'See more'}
                                    </button>
                                  )}
                                </div>
                              )}
                              {themeTags.length > 0 && (
                                <div className="curated-book-tags">
                                  <span className="curated-tag label">Themes</span>
                                  {themeTags.slice(0, 3).map(tag => (
                                    <span key={tag} className="curated-tag">{tag}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            {/* Sentiment buttons - show when book is marked as read */}
                            {isRead && (
                              <div className="book-sentiment-buttons">
                                <button 
                                  className={`sentiment-btn loved ${sentiment === 'loved' ? 'active' : ''}`}
                                  onClick={() => setBookSentiment(book.id, 'loved')}
                                  title="I loved this book!"
                                >
                                  <Heart aria-hidden="true" />
                                </button>
                                <button 
                                  className={`sentiment-btn liked ${sentiment === 'liked' ? 'active' : ''}`}
                                  onClick={() => setBookSentiment(book.id, 'liked')}
                                  title="I liked it"
                                >
                                  <ThumbsUp aria-hidden="true" />
                                </button>
                                <button 
                                  className={`sentiment-btn neutral ${sentiment === 'neutral' ? 'active' : ''}`}
                                  onClick={() => setBookSentiment(book.id, 'neutral')}
                                  title="It was okay"
                                >
                                  <Minus aria-hidden="true" />
                                </button>
                                <button 
                                  className={`sentiment-btn meh ${sentiment === 'meh' ? 'active' : ''}`}
                                  onClick={() => setBookSentiment(book.id, 'meh')}
                                  title="Not for me"
                                >
                                  <ThumbsDown aria-hidden="true" />
                                </button>
                              </div>
                            )}
                            
                            {/* Main action buttons */}
                            {!isRead && (
                              <div className="book-action-buttons">
                                <button 
                                  className={`book-action-btn read-btn ${isRead ? 'active' : ''}`}
                                  onClick={() => toggleBookLike(book)}
                                  title="I've read this book"
                                >
                                  <span className="btn-icon"><BookOpenCheck aria-hidden="true" /></span>
                                  <span className="btn-text">Read</span>
                                </button>
                                <button 
                                  className={`book-action-btn want-btn ${isWantToRead ? 'active' : ''}`}
                                  onClick={() => toggleBookWantToRead(book)}
                                  title="I want to read this book"
                                >
                                  <span className="btn-icon"><BookMarked aria-hidden="true" /></span>
                                  <span className="btn-text">Want</span>
                                </button>
                                <button 
                                  className={`book-action-btn not-interested-btn ${isNotInterested ? 'active' : ''}`}
                                  onClick={(e) => toggleBookDislike(book, e)}
                                  title="Not interested in this book"
                                >
                                  <span className="btn-icon"><X aria-hidden="true" /></span>
                                  <span className="btn-text">Skip</span>
                                </button>
                              </div>
                            )}
                            
                            {/* Clear selection button when read */}
                            {isRead && (
                              <div className="book-clear-selection">
                                <button 
                                  className="clear-btn"
                                  onClick={() => toggleBookLike(book)}
                                  title="Clear selection"
                                >
                                  <X aria-hidden="true" /> Clear
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="liked-disliked-summary">
                      {likedBooks.length > 0 && (
                        <span className="read-count">
                          <BookOpenCheck aria-hidden="true" /> {likedBooks.length} read
                          {Object.values(bookSentiments).filter(s => s === 'loved').length > 0 && 
                            ` (${Object.values(bookSentiments).filter(s => s === 'loved').length} loved)`
                          }
                        </span>
                      )}
                      {wantToReadBooks.length > 0 && (
                        <span className="want-count"><BookMarked aria-hidden="true" /> {wantToReadBooks.length} want to read</span>
                      )}
                      {dislikedBooks.length > 0 && (
                        <span className="disliked-count"><X aria-hidden="true" /> {dislikedBooks.length} skipped</span>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="no-books-message">
                    <p>No books match your criteria. Try different genres, themes, or eras!</p>
                  </div>
                )}
                <div className="discovery-actions">
                  <button className="discovery-back-btn" onClick={prevDiscoveryStep}>
                    ← Back
                  </button>
                  <button 
                    className="discovery-next-btn"
                    onClick={nextDiscoveryStep}
                    disabled={discoveryLoading}
                  >
                    {(likedBooks.length > 0 || wantToReadBooks.length > 0) ? 'Get Recommendations →' : 'Skip & Get Recommendations →'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Results */}
            {discoveryStep === 3 && (
              <div className="discovery-step">
                <h2>Your Personalized Recommendations</h2>
                <p className="step-hint">Based on your preferences — click any book to search for listings on BiblioQuire</p>
                
                {/* Summary of what led to these recommendations */}
                <div className="recommendation-criteria">
                  <span className="criteria-label">Based on:</span>
                  <span className="criteria-item">{selectedGenres.map(id => getDiscoveryGenreName(id)).join(', ')}</span>
                  {likedBooks.length > 0 && (
                    <span className="criteria-item">• {likedBooks.length} book{likedBooks.length > 1 ? 's' : ''} you've read</span>
                  )}
                  {wantToReadBooks.length > 0 && (
                    <span className="criteria-item">• {wantToReadBooks.length} book{wantToReadBooks.length > 1 ? 's' : ''} you want to read</span>
                  )}
                </div>
                
                {discoveryLoading ? (
                  <div className="loading-spinner">
                    <div className="spinner"></div>
                    <span>{LOADING_MESSAGES[loadingMessageIndex]}</span>
                  </div>
                ) : (recommendations.wantToRead?.length > 0 || recommendations.recommended?.length > 0) ? (
                  <div className="recommendations-categorized">
                    
                    {/* Want to Read Section */}
                    {recommendations.wantToRead && recommendations.wantToRead.length > 0 && (
                      <div className="rec-category-section">
                        <h3 className="rec-category-title"><BookMarked size={20} /> Your Reading List</h3>
                        <p className="rec-category-subtitle">Books you marked as want to read</p>
                        <div className="rec-carousel-wrapper">
                          <div className="rec-carousel">
                            {recommendations.wantToRead.map((book, index) => {
                              const bookKey = getBookKey(book);
                              return (
                                <div 
                                  key={`${bookKey}-${index}`} 
                                  className="rec-carousel-item"
                                  onClick={() => window.open(buildBiblioquireSearchUrl(book), '_blank', 'noopener')}
                                >
                                  <div className="rec-image">
                                    <BookCover src={book.imageUrl} title={book.title} author={book.author} />
                                  </div>
                                  <div className="rec-info">
                                    <h4 className="rec-title">{book.title}</h4>
                                    {book.author && <p className="rec-author">by {book.author}</p>}
                                    {book.coreThemes && book.coreThemes.length > 0 && (
                                      <div className="rec-tags">
                                        {book.coreThemes.slice(0, 3).map(theme => (
                                          <span key={theme} className="rec-tag">{theme}</span>
                                        ))}
                                      </div>
                                    )}
                                    <button 
                                      className={`add-to-list-btn ${myList.some(b => getBookKey(b) === bookKey) ? 'in-list' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleMyList(book);
                                      }}
                                    >
                                      {myList.some(b => getBookKey(b) === bookKey) ? '✓ In My List' : '+ Add to List'}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Recommended For You Section */}
                    {recommendations.recommended && recommendations.recommended.length > 0 && (
                      <div className="rec-category-section">
                        <h3 className="rec-category-title"><Sparkles size={20} /> Recommended For You</h3>
                        <p className="rec-category-subtitle">
                          New discoveries based on your preferences
                        </p>
                        <div className="rec-carousel-wrapper">
                          <div className="rec-carousel">
                            {recommendations.recommended.map((book, index) => {
                              const bookKey = getBookKey(book);
                              return (
                                <div 
                                  key={`${bookKey}-${index}`} 
                                  className="rec-carousel-item"
                                  onClick={() => window.open(buildBiblioquireSearchUrl(book), '_blank', 'noopener')}
                                >
                                  <div className="rec-image">
                                    <BookCover src={book.imageUrl} title={book.title} author={book.author} />
                                  </div>
                                  <div className="rec-info">
                                    <h4 className="rec-title">{book.title}</h4>
                                    {book.author && <p className="rec-author">by {book.author}</p>}
                                    {book.coreThemes && book.coreThemes.length > 0 && (
                                      <div className="rec-tags">
                                        {book.coreThemes.slice(0, 3).map(theme => (
                                          <span key={theme} className="rec-tag">{theme}</span>
                                        ))}
                                      </div>
                                    )}
                                    <button 
                                      className={`add-to-list-btn ${myList.some(b => getBookKey(b) === bookKey) ? 'in-list' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleMyList(book);
                                      }}
                                    >
                                      {myList.some(b => getBookKey(b) === bookKey) ? '✓ In My List' : '+ Add to List'}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                    
                  </div>
                ) : (
                  <div className="empty">
                    <p>We couldn't find enough matches. Try different genres or themes!</p>
                  </div>
                )}
                
                {/* My List Panel */}
                {myList.length > 0 && (
                  <div className="my-list-panel">
                    <div className="my-list-header">
                      <h3>📋 My List ({myList.length} book{myList.length !== 1 ? 's' : ''})</h3>
                      <button className="copy-list-btn" onClick={copyListToClipboard}>
                        {listCopied ? '✓ Copied!' : '📋 Copy to Clipboard'}
                      </button>
                    </div>
                    <div className="my-list-books">
                      {myList.map((book, index) => (
                        <div key={`list-${getBookKey(book)}-${index}`} className="my-list-item">
                          <span className="my-list-number">{index + 1}.</span>
                          <span className="my-list-title">{book.title}</span>
                          <span className="my-list-author">by {book.author || 'Unknown'}</span>
                          <button 
                            className="remove-from-list-btn"
                            onClick={() => toggleMyList(book)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="discovery-actions">
                  <button className="discovery-back-btn" onClick={prevDiscoveryStep}>
                    ← Back
                  </button>
                  <button className="discovery-restart-btn" onClick={startDiscovery}>
                    Start Over
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* ABOUT VIEW */}
        {view === 'about' && (
          <section className="about-section">
            <div className="about-content">
              <h1 className="about-title">About BiblioQuire</h1>
              
              <div className="about-intro">
                <p>
                  BiblioQuire is a free search engine for rare, vintage, and collectible books. We aggregate 
                  listings from major online marketplaces to help book collectors, dealers, and enthusiasts 
                  find the books they are looking for in one place.
                </p>
              </div>

              <div className="about-section-block">
                <h2>For Collectors</h2>
                <p>
                  BiblioQuire is built with serious book collectors in mind. Our advanced search filters 
                  let you narrow down results to find exactly what you need: first editions, signed copies, 
                  books with dust jackets, and more. You can exclude ex-library copies and book club 
                  editions to focus on the items that matter to you.
                </p>
              </div>

              <div className="about-section-block faq-section">
                <h2>Frequently Asked Questions</h2>
                
                <div className="faq-item">
                  <h3>How does BiblioQuire work?</h3>
                  <p>
                    When you search on BiblioQuire, we query multiple book marketplaces simultaneously and 
                    present the results in a unified interface. You can filter by price, condition, publication 
                    year, and more. Clicking on any listing takes you directly to the seller's page to complete 
                    your purchase.
                  </p>
                </div>

                <div className="faq-item">
                  <h3>Is BiblioQuire free?</h3>
                  <p>
                    Yes! BiblioQuire is completely free. We do not charge any fees to search, browse, 
                    or access listings. All purchases are made directly with the sellers on their platforms.
                  </p>
                </div>

                <div className="faq-item">
                  <h3>Where do the listings come from?</h3>
                  <p>
                    Currently, we aggregate listings from eBay and supplement with metadata from Google Books 
                    and ISBNdb. We are working to add more sources including AbeBooks, Alibris, and independent 
                    booksellers.
                  </p>
                </div>

                <div className="faq-item">
                  <h3>What is the Discovery tool?</h3>
                  <p>
                    Not sure what to read next? The Discovery tool helps you find books based on your 
                    preferences. Select your favorite genres, themes, and eras, then rate sample books. 
                    The more you rate, the better your personalized recommendations will be.
                  </p>
                </div>

                <div className="faq-item">
                  <h3>Can I save books to a wishlist?</h3>
                  <p>
                    User accounts with wishlists and saved searches are coming soon! For now, you can 
                    use the "Add to List" feature in the Discovery tool to copy your recommended books 
                    to your clipboard.
                  </p>
                </div>

                <div className="faq-item">
                  <h3>What does "antiquarian" mean?</h3>
                  <p>
                    Antiquarian books are generally considered to be books printed before 1900, though 
                    the term is sometimes used more broadly for any old, rare, or collectible books. 
                    BiblioQuire helps you find both antiquarian and modern collectible books.
                  </p>
                </div>

                <div className="faq-item">
                  <h3>How can I contact BiblioQuire?</h3>
                  <p>
                    Have questions, feedback, or suggestions? We would love to hear from you! 
                    Reach out to us at developer@aether-names.com.
                  </p>
                </div>
              </div>

              <div className="about-cta">
                <p>Ready to find your next great book?</p>
                <button className="about-cta-btn" onClick={() => setView('home')}>
                  Start Searching
                </button>
              </div>
            </div>
          </section>
        )}

        {error && <div className="empty warning">{error}</div>}
      </main>

      <footer>
        <div className="footer-content">
          <div className="footer-brand">
            <div className="footer-logo">BiblioQuire</div>
            <p className="footer-desc">
              Find books from eBay and other marketplaces, all in one place.
            </p>
          </div>
          <div className="footer-section">
            <h4>Explore</h4>
            <ul className="footer-links">
              <li><a href="#browse" onClick={(e) => { e.preventDefault(); setView('browse'); setSelectedCategory(''); }}>Browse Genres</a></li>
              <li><a href="#search" onClick={(e) => { e.preventDefault(); setView('search'); }}>Search Books</a></li>
              <li><a href="#discover" onClick={(e) => { e.preventDefault(); startDiscovery(); }}>Discovery Tool</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Sources</h4>
            <ul className="footer-links">
              <li><a href="https://www.ebay.com" target="_blank" rel="noreferrer">eBay</a></li>
              <li><a href="https://books.google.com" target="_blank" rel="noreferrer">Google Books</a></li>
              <li><a href="https://isbndb.com" target="_blank" rel="noreferrer">ISBNdb</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>About</h4>
            <ul className="footer-links">
              <li><a href="#about" onClick={(e) => { e.preventDefault(); setView('about'); }}>About BiblioQuire</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="footer-colophon">Set in Fell English, Garamond &amp; Spectral</div>
          <div className="footer-copyright">© MMXXVI BiblioQuire · All rights reserved</div>
        </div>
      </footer>

      {/* Book Detail Modal */}
      {selectedBook && (
        <BookDetailModal book={selectedBook} onClose={() => setSelectedBook(null)} />
      )}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button className="scroll-to-top" onClick={scrollToTop} title="Back to top">
          ↑
        </button>
      )}
    </div>
  );
}

export default App;
